<template>
  <div id="insightsDashboard" class="divContainer">
    <div class="divContentInsights margin-subpages">
      <div class="top-contariner flex">
        <div class="engagement">
          <div class="fs-15 fw-700 flex-center">
            {{ $t("insights.Engagement_and_Impact") }}
            <el-tooltip class="header-table-tooltip" placement="top">
              <div slot="content" class="p-5">
                <div style="max-width: 250px">
                  {{ $t("insights.Highlighting_key_engagement_metrics") }}
                </div>
              </div>
              <img :src="$utils('icons', 'ic_help_outline-16px')" class="ml-6 cursor-pointer"
                alt="icons_ic_help_outline-16px" />
            </el-tooltip>
            <div class="flex-center"></div>
          </div>
          <div class="card-contariner">
            <div class="flex">
              <div class="engagement-left">
                <div class="section-header">
                  <span class="flex-center router-link section-title" @click="routerLink('insightsWithTab', 'people')">
                    <span>{{ $t('insights.people_impact') }}</span>
                    <i class="el-icon-arrow-right"></i>
                  </span>
                </div>
                <div v-if="engagementImpactLoadingStatus">
                  <div class="" style="height: 50px"></div>
                  <div class="fs-10 mt-2 text-skeleton" style="width: 90%">
                    &nbsp;
                  </div>
                </div>
                <div v-else>
                  <el-tooltip placement="top" effect="light">
                    <div slot="content" style="max-width: 320px; padding: 12px;">
                      <div class="fw-700 fs-13 mb-2">{{ $t('insights.workplace_culture_impact') }}</div>
                      <div class="fs-12 mb-3 opa-08">
                        {{ $t('insights.people_impact_tooltip_desc') }}
                      </div>
                      <div class="fs-12">
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-green"></span>
                          <div>
                            <strong>{{ $t('insights.high_impact_label') }}</strong>
                            <div class="fs-11 opa-06">{{ $t('insights.high_impact_desc') }}</div>
                            <div class="fs-11 opa-08 mt-1">{{ engagementpeople.counts.green }} people ({{ Math.round(engagementpeople.counts.green / engagementpeople.total * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-yellow"></span>
                          <div>
                            <strong>{{ $t('insights.moderate_impact_label') }}</strong>
                            <div class="fs-11 opa-06">{{ $t('insights.moderate_impact_desc') }}</div>
                            <div class="fs-11 opa-08 mt-1">{{ engagementpeople.counts.yellow }} people ({{ Math.round(engagementpeople.counts.yellow / engagementpeople.total * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-red"></span>
                          <div>
                            <strong>{{ $t('insights.low_impact_label') }}</strong>
                            <div class="fs-11 opa-06">{{ $t('insights.low_impact_desc') }}</div>
                            <div class="fs-11 opa-08 mt-1">{{ engagementpeople.counts.red }} people ({{ Math.round(engagementpeople.counts.red / engagementpeople.total * 100) }}%)</div>
                          </div>
                        </div>
                      </div>
                      <div class="tooltip-footer">
                        <div class="fs-12 opa-04 mt-8 pt-2" style="border-top: 1px solid rgba(0,0,0,0.1);">
                          <em>{{ $t('insights.people_impact_target') }}</em>
                        </div>
                      </div>
                    </div>
                    <div class="stacked-bar-container">
                      <div class="stacked-bar">
                        <div 
                          v-if="engagementpeople.counts.green > 0"
                          class="bar-segment bar-green" 
                          :style="`width: ${(engagementpeople.counts.green / engagementpeople.total * 100)}%`"
                          :title="`${engagementpeople.counts.green} high impact contributors (${Math.round(engagementpeople.counts.green / engagementpeople.total * 100)}%)`">
                        </div>
                        <div 
                          v-if="engagementpeople.counts.yellow > 0"
                          class="bar-segment bar-yellow" 
                          :style="`width: ${(engagementpeople.counts.yellow / engagementpeople.total * 100)}%`"
                          :title="`${engagementpeople.counts.yellow} medium impact contributors (${Math.round(engagementpeople.counts.yellow / engagementpeople.total * 100)}%)`">
                        </div>
                        <div 
                          v-if="engagementpeople.counts.red > 0"
                          class="bar-segment bar-red" 
                          :style="`width: ${(engagementpeople.counts.red / engagementpeople.total * 100)}%`"
                          :title="`${engagementpeople.counts.red} low impact contributors (${Math.round(engagementpeople.counts.red / engagementpeople.total * 100)}%)`">
                        </div>
                      </div>
                      <div class="bar-legend">
                        <div class="legend-item">
                          <div class="legend-color bg-green"></div>
                          <span class="legend-text">{{ engagementpeople.counts.green }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-yellow"></div>
                          <span class="legend-text">{{ engagementpeople.counts.yellow }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-red"></div>
                          <span class="legend-text">{{ engagementpeople.counts.red }}</span>
                        </div>
                      </div>
                    </div>
                  </el-tooltip>
                  <div class="fs-11 mt-2 metric-summary">
                    <span :class="getPeopleImpactClass()">{{ getPeopleImpactText() }}</span>
                  </div>
                </div>
              </div>
              <div class="engagement-right">
                <div class="section-header">
                  <span class="flex-center router-link section-title" @click="routerLink('insightsWithTab', 'managers')">
                    <span>{{ $t('insights.manager_activity') }}</span>
                    <i class="el-icon-arrow-right"></i>
                  </span>
                </div>
                <div v-if="engagementImpactLoadingStatus">
                  <div class="" style="height: 50px"></div>
                  <div class="fs-10 mt-2 text-skeleton" style="width: 90%">
                    &nbsp;
                  </div>
                </div>
                <div v-else>
                  <el-tooltip placement="top" effect="light">
                    <div slot="content" style="max-width: 320px; padding: 12px;">
                      <div class="fw-700 fs-13 mb-2">{{ $t('insights.manager_feedback_responsiveness') }}</div>
                      <div class="fs-12 mb-3 opa-08">
                        {{ $t('insights.manager_activity_tooltip_desc') }}
                      </div>
                      <div class="fs-12">
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-green"></span>
                          <div>
                            <strong>{{ $t('insights.manager_responsive_label') }}</strong>
                            <div class="fs-11 opa-06">{{ $t('insights.manager_responsive_desc') }}</div>
                            <div class="fs-11 opa-08 mt-1">{{ activeOverview.active }} managers ({{ Math.round(activeOverview.active / activeOverview.all * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-yellow"></span>
                          <div>
                            <strong>{{ $t('insights.manager_moderate_label') }}</strong>
                            <div class="fs-11 opa-06">{{ $t('insights.manager_moderate_desc') }}</div>
                            <div class="fs-11 opa-08 mt-1">{{ activeOverview.passive }} managers ({{ Math.round(activeOverview.passive / activeOverview.all * 100) }}%)</div>
                          </div>
                        </div>
                        <div class="tooltip-legend-item">
                          <span class="tooltip-color bg-red"></span>
                          <div>
                            <strong>{{ $t('insights.manager_minimal_label') }}</strong>
                            <div class="fs-11 opa-06">{{ $t('insights.manager_minimal_desc') }}</div>
                            <div class="fs-11 opa-08 mt-1">{{ activeOverview.inactive }} managers ({{ Math.round(activeOverview.inactive / activeOverview.all * 100) }}%)</div>
                          </div>
                        </div>
                      </div>
                      <div class="tooltip-footer">
                        <div class="fs-12 opa-04 mt-8 pt-2" style="border-top: 1px solid rgba(0,0,0,0.1);">
                          <em>{{ $t('insights.manager_activity_target') }}</em>
                        </div>
                      </div>
                    </div>
                    <div class="stacked-bar-container">
                      <div class="stacked-bar">
                        <div 
                          v-if="activeOverview.active > 0"
                          class="bar-segment bar-green" 
                          :style="`width: ${(activeOverview.active / activeOverview.all * 100)}%`"
                          :title="`${activeOverview.active} active managers (${Math.round(activeOverview.active / activeOverview.all * 100)}%)`">
                        </div>
                        <div 
                          v-if="activeOverview.passive > 0"
                          class="bar-segment bar-yellow" 
                          :style="`width: ${(activeOverview.passive / activeOverview.all * 100)}%`"
                          :title="`${activeOverview.passive} passive managers (${Math.round(activeOverview.passive / activeOverview.all * 100)}%)`">
                        </div>
                        <div 
                          v-if="activeOverview.inactive > 0"
                          class="bar-segment bar-red" 
                          :style="`width: ${(activeOverview.inactive / activeOverview.all * 100)}%`"
                          :title="`${activeOverview.inactive} inactive managers (${Math.round(activeOverview.inactive / activeOverview.all * 100)}%)`">
                        </div>
                      </div>
                      <div class="bar-legend">
                        <div class="legend-item">
                          <div class="legend-color bg-green"></div>
                          <span class="legend-text">{{ activeOverview.active }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-yellow"></div>
                          <span class="legend-text">{{ activeOverview.passive }}</span>
                        </div>
                        <div class="legend-item">
                          <div class="legend-color bg-red"></div>
                          <span class="legend-text">{{ activeOverview.inactive }}</span>
                        </div>
                      </div>
                    </div>
                  </el-tooltip>
                  <div class="fs-11 mt-2 metric-summary">
                    <span :class="getManagerActivityClass()">{{ getManagerActivityText() }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="x-line my-12"></div>
            <div v-if="engagementImpactLoadingStatus">
              <div class="flex-center">
                <div class="fs-13 fw-700 mr-auto">
                  {{ $t("insights.eNPS") }}
                </div>
                <div class="mt-6 fs-10 text-skeleton" style="width: 30%; background: #6c30cc10">
                  &nbsp;
                </div>
              </div>
              <div class="flex-center mt-12">
                <div class="benchmark mt-12">&nbsp;</div>
                <div class="mx-auto" style="width: 74px">
                  <div class="flex-center text-skeleton" style="width: 80%">
                    &nbsp;
                  </div>
                  <div class="fs-11 mt-6 text-skeleton opa-06" style="width: 100%">
                    &nbsp;
                  </div>
                </div>
                <div class="mx-auto" style="width: 62px">
                  <div class="flex-center text-skeleton" style="width: 80%">
                    &nbsp;
                  </div>
                  <div class="fs-11 mt-6 text-skeleton opa-06" style="width: 100%">
                    &nbsp;
                  </div>
                </div>
                <div class="mx-auto" style="width: 73px">
                  <div class="flex-center text-skeleton" style="width: 80%">
                    &nbsp;
                  </div>
                  <div class="fs-11 mt-6 text-skeleton opa-06" style="width: 100%">
                    &nbsp;
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="flex-center">
                <div class="fs-13 fw-700 mr-auto">
                  {{ this.benchmarkBreak }}
                </div>
                <span class="flex-center router-link" @click="routerLink('insightsWithTab', 'report')">
                  <span>{{ $t("insights.Report_Card") }}</span>
                  <i class="el-icon-arrow-right"></i>
                </span>
              </div>

              <div v-if="enps.type == 'eNPS' || enps.type == 'NPS'" class="flex-center mt-12">
                <div :class="`benchmark ${enps.total < benchmarkScoreRed()
                  ? 'red'
                  : enps.total < benchmarkScore()
                    ? 'yellow'
                    : 'green'
                  }`">
                  <el-tooltip 
                    placement="top" 
                    effect="light"
                    :visible-arrow="true"
                    popper-class="score-distribution-tooltip">
                    <div slot="content" style="padding: 10px 40px 10px 20px; min-width: 340px;">
                      <div class="tooltip-title">
                        {{ $t("insights.Score_Distribution") }}
                        <span class="opa-04" style="float: right; margin-right: -20px;">
                          {{ getTotalResponses() }} responses
                        </span>
                      </div>
                      <div class="horizontal-histogram">
                        <div class="score-labels">
                          <div v-for="score in getScoreRange()" :key="score" class="score-label-item">
                            {{ formatScoreLabel(score) }}
                          </div>
                        </div>
                        <div class="histogram-bars">
                          <div v-for="score in getScoreRange()" :key="score" class="histogram-bar" 
                               :style="{
                                 height: getScoreWidth(score),
                                 backgroundColor: getScoreColor(score)
                               }">
                          </div>
                        </div>
                        <div class="score-counts">
                          <div v-for="score in getScoreRange()" :key="score" class="score-count-item">
                            ({{getScoreCount(score)}})
                          </div>
                        </div>
                      </div>
                    </div>
                    <span>{{ enps.total > 0 ? "+" + enps.total : enps.total }}</span>
                  </el-tooltip>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.promotors : 0 }}%
                    <img class="ml-6" :src="$utils('icons', 'Promoters_16')" alt="icons_Promoters_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Promoters") }} ({{
                      enps.promotors ? enps.promotors : 0
                    }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.passives : 0 }}%
                    <img class="ml-6" :src="$utils('icons', 'Passives_16')" alt="icons_Passives_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Passives") }} ({{
                      enps.passives ? enps.passives : 0
                    }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.detractors : 0 }}%
                    <img class="ml-6" :src="$utils('icons', 'Distractors_16')" alt="icons_Distractors_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Detractors") }} ({{
                      enps.detractors ? enps.detractors : 0
                    }})
                  </div>
                </div>
              </div>
              <div v-else class="flex-center mt-12">
                <div :class="`benchmark ${enps.total < benchmarkScoreRed()
                    ? 'red'
                    : enps.total < benchmarkScore()
                      ? 'yellow'
                      : 'green'
                  }`">
                  <span v-if="checkedInxed">{{
                    enps.total > 0 ? enps.total : enps.total
                  }}</span>
                  <span v-else>{{
                    enps.total > 0 ? "+" + enps.total : enps.total
                  }}</span>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.excellent : 0 }}%
                    <img class="ml-6" :src="$utils('icons', imagesBenchmark('Excellent'))" alt="icons_Promoters_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Excellent") }} ({{
                      enps.excellent ? enps.excellent : 0
                    }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.fair : 0 }}%
                    <img class="ml-6" :src="$utils('icons', imagesBenchmark('Fair'))" alt="icons_Passives_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{ $t("insights.Fair") }} ({{ enps.fair ? enps.fair : 0 }})
                  </div>
                </div>
                <div class="mx-auto">
                  <div class="flex-center fs-14 fw-700">
                    {{ enps.percent ? enps.percent.critical : 0 }}%
                    <img class="ml-6" :src="$utils('icons', imagesBenchmark('Critical'))" alt="icons_Distractors_16" />
                  </div>
                  <div class="fs-11 fw-700 opa-06 mt-4">
                    {{
                      benchmarkBreak == "WHO-5 Wellbeing Index"
                      ? $t("insights.Poor")
                      : $t("insights.Critical")
                    }}
                    ({{ enps.critical ? enps.critical : 0 }})
                  </div>
                </div>
              </div>

              <div class="flex-center fs-12 mt-16">
                <div v-if="enps.total == benchmarkScore()">
                  <span class="fc-000000 fw-400">
                    {{ $t("insights.point_same_benchmark") }} (+{{
                      benchmarkScore()
                    }})</span>
                </div>
                <div v-else>
                  <span class="fw-900 fc-1AC362" v-if="enps.total > benchmarkScore()">{{ enps.total - benchmarkScore() }}
                    {{ $t("insights.points") }}

                    <span class="fc-000000 fw-400">{{ $t("insights.above_benchmark") }} (<span
                        v-if="!checkedInxed">+</span>{{ benchmarkScore() }})</span>
                  </span>
                  <span v-else class="fw-900 fc-F44336">{{
                    isNaN(enps.total)
                    ? 0
                    : (enps.total - benchmarkScore()) * -1
                  }}
                    {{ $t("insights.points") }}

                    <span class="fc-000000 fw-400">{{ $t("insights.below_benchmark") }} (<span
                        v-if="!checkedInxed">+</span>{{ benchmarkScore() }})</span>
                  </span>
                </div>
                <div class="ml-auto">
                  <div class="point-bar">
                    <div class="value" :style="`width: ${benchmarkBar(enps.total)}px; ${enps.total < 0
                      ? ' background-color: #F44336;'
                      : enps.total < benchmarkScore()
                        ? ' background-color: #FBC02D;'
                        : ''
                      }`"></div>
                    <div class="value benchmark" :style="`width: ${benchmarkBar(benchmarkScore())}px;`"></div>
                  </div>
                  <div v-if="checkedInxed" class="flex-center opa-04 fs-10 fw-700 mt-2">
                    <div>0</div>
                    <div class="ml-auto">100</div>
                  </div>
                  <div v-else class="flex-center opa-04 fs-10 fw-700 mt-2">
                    <div>-100</div>
                    <div class="ml-auto">+100</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="weekly-status">
          <div class="fs-15 fw-700">{{ dateshow }}</div>
          <div class="card-contariner">
            <div class="flex">
              <div>
                <div class="fs-12 fw-700" style="width: 96px">
                  {{ $t("insights.Happiness_Rate") }}
                </div>
                <div v-if="weeklyLoadingStatus" class="flex-center mt-6">
                  <div class="image-skeleton opa-08" style="width: 24px; height: 24px; border-radius: 24px">
                    &nbsp;
                  </div>
                  <div class="text-skeleton ml-6" style="width: 40px">
                    &nbsp;
                  </div>
                </div>
                <div v-else class="flex-center mt-6">
                  <img :src="$utils('icons', 'Happiness_Rate_24')" alt="icons_Happiness_Rate_24" />
                  <div class="flex-center">
                    <div class="fs-16 fw-700 ml-6">
                      {{ `${happiness.happy}%` }}
                    </div>
                    <div v-if="happiness.calculatehappy != 0" :class="`label-change ${0 > happiness.calculatehappy ? 'fc-F44336' : 'fc-1AC362'
                      }`">
                      {{
                        happiness.calculatehappy > 0
                        ? `+${happiness.calculatehappy}`
                        : `${happiness.calculatehappy}`
                      }}
                      pts
                    </div>
                    <div v-else :class="`label-change fc-6A6A6A`">
                      {{ `+${happiness.calculatehappy}` }} pts
                    </div>
                  </div>
                </div>
              </div>
              <div class="ml-12">
                <div class="fs-12 fw-700">{{ $t("insights.Risk_Rate") }}</div>
                <div v-if="weeklyLoadingStatus" class="flex-center mt-6">
                  <div class="image-skeleton opa-08" style="width: 24px; height: 24px; border-radius: 24px">
                    &nbsp;
                  </div>
                  <div class="text-skeleton ml-6" style="width: 40px">
                    &nbsp;
                  </div>
                </div>
                <div v-else class="flex-center mt-6">
                  <img :src="$utils('icons', 'Risk_Rate_v3_24')" alt="icons_Risk_Rate_v3_24" />
                  <div class="fs-16 fw-700 ml-6">
                    {{ `${happiness.worse}%` }}
                  </div>
                  <div v-if="happiness.calculaterisk != 0" :class="`label-change ${happiness.calculaterisk * 1 > 0
                    ? 'fc-F44336'
                    : 'fc-1AC362'
                    }`">
                    {{
                      happiness.calculaterisk > 0
                      ? `+${happiness.calculaterisk}`
                      : `${happiness.calculaterisk}`
                    }}
                    pts
                  </div>
                  <div v-else :class="`label-change fc-1AC362`">
                    {{ `+${happiness.calculaterisk}` }} pts
                  </div>
                </div>
              </div>
            </div>
            <div v-if="weeklyLoadingStatus" class="text-skeleton fs-12 mt-12" style="width: 95px; background: #6c30cc10">
              &nbsp;
            </div>
            <span v-else class="flex-center router-link mt-12" @click="routerLink('insightsWithTab', 'teams')">
              <span>{{ $t("insights.View_by_team") }}</span>
              <i class="el-icon-arrow-right"></i>
            </span>
            <div class="x-line my-12"></div>
            <div class="mt-24">
              <div class="fs-12 fw-700">
                {{ $t("insights.Open_ended_Feedback") }}
              </div>
              <div class="fs-12">
                <div v-if="weeklyLoadingStatus" class="flex-center mt-6">
                  <div class="image-skeleton opa-08" style="width: 24px; height: 24px; border-radius: 24px">
                    &nbsp;
                  </div>
                  <div class="text-skeleton ml-6" style="width: 40px">
                    &nbsp;
                  </div>
                </div>
                <div v-else class="flex-center my-6">
                  <img width="32" :src="$utils('icons', 'Open-ended_Feedback_24@2x')"
                    alt="icons_Open-ended_Feedback_24@2x" />
                  <div class="fs-16 fw-700 ml-6">
                    {{ `${feedback.feedback}%` }}
                  </div>
                  &nbsp;
                  <div v-if="feedback.calculate != 0" :class="`label-change ${feedback.calculate > 0 ? 'fc-1AC362' : 'fc-F44336'
                    }`">
                    {{
                      feedback.calculate > 0
                      ? `+${feedback.calculate}`
                      : `${feedback.calculate}`
                    }}
                    pts
                  </div>
                  <div v-else :class="`label-change fc-6A6A6A`">
                    {{ `+${feedback.calculate}` }} pts
                  </div>
                </div>
                <div v-if="weeklyLoadingStatus" class="text-skeleton mt-6" style="width: 140px">
                  &nbsp;
                </div>
                <div v-else>
                  <div v-if="feedback.feedback - 40 >= 0">
                    <span class="fw-900 fc-1AC362">{{ feedback.feedback - 40 }}% {{ $t("insights.points") }}
                    </span>
                    <span>{{ $t("insights.above_benchmark") }} (40%)</span>
                  </div>
                  <div v-else>
                    <span class="fw-900 fc-F44336">{{
                      isNaN(feedback.feedback)
                      ? "-"
                      : (feedback.feedback - 40) * -1
                    }}% {{ $t("insights.points") }}
                    </span>
                    <span>{{ $t("insights.below_benchmark") }} (40%)</span>
                  </div>
                </div>
              </div>
            </div>
            <span v-if="!weeklyLoadingStatus && questionOpenEnd && questionOpenEnd.question_id" class="flex-center router-link mt-12" @click="
              routerLink('questions', 'standard', questionOpenEnd.question_id)
              ">
              <span class="relative">
                <div class="ellipsis-3">{{ questionOpenEnd.question_en }}</div>
              </span>
              <i class="el-icon-arrow-right ml-auto"></i>
            </span>
          </div>
        </div>

        <div class="activity">
          <div class="fs-15 fw-700">{{ $t("insights.Activity") }}</div>
          <div class="card-contariner">
            <div class="flex-center">
              <div class="fs-12 fw-700 mr-auto">
                {{ $t("insights.Adoption") }}
              </div>
              <div v-if="activityLoadingStatus" class="text-skeleton fs-12" style="width: 60px; background: #6c30cc10">
                &nbsp;
              </div>
              <span v-else class="flex-center router-link" @click="routerLink('members', 'members')">
                <span>{{ $t("insights.Members") }}</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div class="mt-6">
              <div v-if="activityLoadingStatus" class="bar-contariner flex-center opa-06">
                <div v-for="(value, index) in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]" :key="index + '_bar_mock_one'"
                  :class="`bar ${value}`"></div>
                <div class="text-skeleton ml-6" style="width: 40px">&nbsp;</div>
              </div>
              <div v-else class="bar-contariner flex-center">
                <div v-for="(value, index) in arrayToPercentage(adoption)" :key="index + '_bar_adoption'"
                  :class="`bar ${value}`"></div>
                <div class="fs-16 fw-700 ml-6">{{ adoption }}%</div>
              </div>
            </div>
            <div v-if="activityLoadingStatus" class="text-skeleton mt-6 fs-12 opa-08" style="width: 180px">
              &nbsp;
            </div>
            <div v-else class="mt-6 fs-12">
              <div v-if="adoption - 90 >= 0">
                <span class="fw-900 fc-1AC362">{{ adoption - 90 ? (adoption - 90).toFixed() : 0 }}%
                  {{ $t("insights.points") }}
                </span>
                <span class="fc-000000 fw-400">{{ $t("insights.above_benchmark") }} (90%)</span>
              </div>
              <div v-else>
                <span class="fw-900 fc-F44336">{{
                  (adoption - 90) * -1 ? ((adoption - 90) * -1).toFixed() : 0
                }}% {{ $t("insights.points") }}
                </span>
                <span class="fc-000000 fw-400">{{ $t("insights.below_benchmark") }} (90%)</span>
              </div>
            </div>

            <div class="x-line my-14"></div>

            <div class="flex-center mt-24">
              <div class="fs-12 fw-700 mr-auto">
                {{ $t("insights.Monthly_Active_Users") }}
              </div>
            </div>
            <div class="mt-6">
              <div v-if="activityLoadingStatus" class="bar-contariner flex-center opa-06">
                <div v-for="(value, index) in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]" :key="index + '_bar_mock_one'"
                  :class="`bar ${value}`"></div>
                <div class="text-skeleton ml-6" style="width: 40px">&nbsp;</div>
              </div>
              <div v-else class="bar-contariner flex-center">
                <div v-for="(value, index) in arrayToPercentage(
                  monthlyActiveUser.bar
                )" :key="index + '_bar_adoption'" :class="`bar ${value}`"></div>
                <div class="fs-16 fw-700 ml-6">
                  {{ monthlyActiveUser.activeCurrent }}
                </div>
                <div v-if="monthlyActiveUser.percent_change != 0" :class="`label-change ${0 > monthlyActiveUser.percent_change
                  ? 'fc-F44336'
                  : 'fc-1AC362'
                  }`">
                  {{
                    monthlyActiveUser.percent_change
                    ? monthlyActiveUser.percent_change > 0
                      ? `+${monthlyActiveUser.percent_change}`
                      : monthlyActiveUser.percent_change
                    : 0
                  }}%
                </div>

                <div v-else :class="`label-change fc-6A6A6A`">
                  {{ `+${monthlyActiveUser.percent_change}` }}%
                </div>
              </div>
            </div>
            <div class="flex-center mt-16">
              <div class="fs-12 fw-700 mr-auto">
                {{ $t("insights.Weekly_Active_Users") }}
              </div>
            </div>
            <div class="mt-6">
              <div v-if="activityLoadingStatus" class="bar-contariner flex-center opa-04">
                <div v-for="(value, index) in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]" :key="index + '_bar_mock_one'"
                  :class="`bar ${value}`"></div>
                <div class="text-skeleton ml-6" style="width: 30px">&nbsp;</div>
              </div>
              <div v-else class="bar-contariner flex-center">
                <div v-for="(value, index) in arrayToPercentage(
                  weeklyActiveUser.bar
                )" :key="index + '_bar_adoption'" :class="`bar ${value}`"></div>
                <div class="fs-16 fw-700 ml-6">
                  {{ weeklyActiveUser.activeCurrent }}
                </div>
                <div v-if="weeklyActiveUser.percent_change != 0" :class="`label-change ${0 > weeklyActiveUser.percent_change
                  ? 'fc-F44336'
                  : 'fc-1AC362'
                  }`">
                  {{
                    weeklyActiveUser.percent_change
                    ? weeklyActiveUser.percent_change > 0
                      ? `+${weeklyActiveUser.percent_change}`
                      : weeklyActiveUser.percent_change
                    : 0
                  }}%
                </div>
                <div v-else :class="`label-change fc-6A6A6A`">
                  {{ `+${weeklyActiveUser.percent_change}` }}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Focus, Goals & Learning Section -->
      <div class="focus-alignment-section mt-24">
        <!-- Section Headers Row -->
        <div class="section-headers-row">
          <!-- Focus & Goal Alignment Header -->
          <div class="section-header-group section-header-focus">
            <div class="flex">
              <span class="fs-15 fw-700">{{ $t('insights.focus_and_goal_alignment') }}</span>
              <el-tooltip effect="dark" placement="top">
                <div class="tooltipHelpStyle" slot="content">
                  <div style="margin-bottom: 8px;">{{ $t('insights.focus_alignment_tooltip') }}</div>
                  <div style="font-size: 11px; opacity: 0.8;">
                    <div>{{ $t('insights.focus_coverage_tooltip_bullet') }}</div>
                    <div>{{ $t('insights.goal_alignment_tooltip_bullet') }}</div>
                  </div>
                </div>
                <div style="padding-left: 8px; display: flex; align-items: center">
                  <img :src="getImgUrl('help_outline-16px')" />
                </div>
              </el-tooltip>
            </div>
          </div>
          <!-- Learning Header -->
          <div class="section-header-group section-header-learning">
            <div class="flex">
              <span class="fs-15 fw-700">{{ $t('insights.learning_section_title') }}</span>
              <el-tooltip effect="dark" placement="top">
                <div class="tooltipHelpStyle" slot="content">
                  <div style="margin-bottom: 8px;">{{ $t('insights.learning_tooltip') }}</div>
                  <div style="font-size: 11px; opacity: 0.8;">
                    <div>{{ $t('insights.learning_progress_tooltip_bullet') }}</div>
                  </div>
                </div>
                <div style="padding-left: 8px; display: flex; align-items: center">
                  <img :src="getImgUrl('help_outline-16px')" />
                </div>
              </el-tooltip>
            </div>
          </div>
        </div>
        <!-- Cards Row -->
        <div class="focus-alignment-cards">
          <!-- Focus Coverage Card -->
          <div class="focus-card">
            <div class="focus-card-header">
              <span class="fs-12 fw-700">{{ $t('insights.focus_coverage') }}</span>
              <span class="flex-center router-link" @click="navigateToPerformance('focus-areas')">
                <span>{{ $t('insights.focus_areas') }}</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div v-if="focusCoverageLoading" class="focus-card-loading">
              <div class="text-skeleton" style="width: 60px; height: 32px; margin-bottom: 8px;">&nbsp;</div>
              <div class="text-skeleton" style="width: 100%; height: 8px;">&nbsp;</div>
            </div>
            <div v-else class="focus-card-content">
              <div class="focus-metric">
                <span class="focus-metric-value" :style="{ color: focusCoverageHealthColor }">{{ focusCoverageRate }}%</span>
                <div v-if="focusCoverageLastWeekComparison" class="weekly-change-wrapper">
                  <div class="weekly-metric-row">
                    <span class="weekly-value">{{ Math.round(focusCoverageLastWeekComparison.previous) }}%</span>
                    <span 
                      class="label-change"
                      :class="{
                        'text-green': focusCoverageLastWeekComparison.trend === 'up',
                        'text-red': focusCoverageLastWeekComparison.trend === 'down',
                        'fc-6A6A6A': focusCoverageLastWeekComparison.trend === 'neutral'
                      }"
                    >{{ focusCoverageLastWeekComparison.change > 0 ? '+' : '' }}{{ focusCoverageLastWeekComparison.change }} pts</span>
                  </div>
                  <span class="change-label">{{ $t('insights.last_week') }}</span>
                </div>
              </div>
              <div class="focus-progress-bar">
                <div
                  class="focus-progress-fill"
                  :style="{
                    width: Math.max(focusCoverageRate, 2) + '%',
                    backgroundColor: focusCoverageHealthColor
                  }"
                ></div>
              </div>
              <div class="focus-metric-label">
                {{ focusSummaryStats ? focusSummaryStats.withFocus : 0 }} {{ $t('insights.people_with_focus_connector') }} {{ focusSummaryStats ? focusSummaryStats.totalPeople : 0 }} {{ $t('insights.people_with_focus_suffix') }}
              </div>
            </div>
          </div>

          <!-- Goal Alignment Card -->
          <div class="focus-card">
            <div class="focus-card-header">
              <span class="fs-12 fw-700">{{ $t('insights.goal_alignment') }}</span>
              <span class="flex-center router-link" @click="navigateToPerformance('goals')">
                <span>{{ $t('insights.goals_label') }}</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div v-if="focusAlignmentLoading" class="focus-card-loading">
              <div class="text-skeleton" style="width: 60px; height: 32px; margin-bottom: 8px;">&nbsp;</div>
              <div class="text-skeleton" style="width: 100%; height: 8px;">&nbsp;</div>
            </div>
            <div v-else class="focus-card-content">
              <template v-if="isGoalAlignmentUnknown">
                <div class="focus-metric">
                  <span class="focus-metric-value focus-metric-value-unknown">{{ $t('insights.unknown_label') }}</span>
                </div>
                <div class="focus-progress-bar">
                  <div class="focus-progress-fill" style="width: 0%;"></div>
                </div>
                <div class="focus-metric-label">
                  {{ goalAlignmentDisplay.unknownItems }} {{ $t('insights.unknown_suffix') }}
                </div>
              </template>
              <template v-else-if="isGoalAlignmentApplicable">
                <div class="focus-metric">
                  <span class="focus-metric-value" :style="{ color: goalAlignmentHealthColor }">{{ Math.round(goalAlignmentRate) }}%</span>
                  <div v-if="goalAlignmentWeekChange && goalAlignmentWeekChange.trend !== 'no_data'" class="weekly-change-wrapper">
                    <div class="weekly-metric-row">
                      <span class="weekly-value">{{ goalAlignmentWeekChange.current !== null ? Math.round(goalAlignmentWeekChange.current) : 0 }}%</span>
                      <span 
                        class="label-change"
                        :class="{
                          'text-green': goalAlignmentWeekChange.trend === 'up',
                          'text-red': goalAlignmentWeekChange.trend === 'down',
                          'fc-6A6A6A': goalAlignmentWeekChange.trend === 'neutral'
                        }"
                      >{{ goalAlignmentWeekChange.change > 0 ? '+' : '' }}{{ goalAlignmentWeekChange.change }}%</span>
                    </div>
                    <span class="change-label">{{ $t('insights.last_week') }}</span>
                  </div>
                </div>
                <div class="focus-progress-bar">
                  <div 
                    class="focus-progress-fill"
                    :style="{ 
                      width: Math.max(goalAlignmentRate, 2) + '%', 
                      backgroundColor: goalAlignmentHealthColor 
                    }"
                  ></div>
                </div>
                <div class="focus-metric-label">
                  {{ focusAlignmentData ? focusAlignmentData.alignedItems : 0 }} {{ $t('insights.aligned_suffix') }} / {{ focusAlignmentData ? focusAlignmentData.unalignedItems : 0 }} {{ $t('insights.unaligned_suffix') }}
                </div>
              </template>
              <template v-else>
                <div class="focus-metric">
                  <span class="focus-metric-value" style="color: #dcdfe6;">N/A</span>
                </div>
                <div class="focus-progress-bar">
                  <div class="focus-progress-fill" style="width: 0%;"></div>
                </div>
                <div class="focus-na-message">
                  <i class="el-icon-info"></i>
                  <span>{{ $t('insights.alignment_not_available') }}</span>
                </div>
              </template>
            </div>
          </div>

          <!-- Learning Progress Card -->
          <div class="focus-card">
            <div class="focus-card-header">
              <span class="fs-12 fw-700">{{ $t('insights.learning_progress') }}</span>
              <span class="flex-center router-link" @click="navigateToSkills('learning-journeys')">
                <span>{{ $t('insights.learning_journeys') }}</span>
                <i class="el-icon-arrow-right"></i>
              </span>
            </div>
            <div v-if="learningLoading" class="focus-card-loading">
              <div class="text-skeleton" style="width: 60px; height: 32px; margin-bottom: 8px;">&nbsp;</div>
              <div class="text-skeleton" style="width: 100%; height: 8px;">&nbsp;</div>
            </div>
            <div v-else class="focus-card-content">
              <div class="focus-metric">
                <span class="focus-metric-value" style="color: #6C30CC;">{{ learningOverview.upskilling }}</span>
              </div>
              <div class="focus-metric-label">
                {{ $t('insights.members_upskilling') }}
              </div>
              <div class="focus-metric-label learning-stats">
                <span class="learning-stat">
                  <span class="learning-stat-value">{{ learningOverview.active }}</span> {{ $t('insights.active') }}
                </span>
                <span class="learning-stat-separator">·</span>
                <span class="learning-stat">
                  <span class="learning-stat-value" style="color: #35D077;">{{ learningOverview.completed }}</span> {{ $t('insights.completed_label') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="grid-2-tenure mt-40">
        <div style="justify-content: flex-start">
          <div class="flex">
            <div>
              <span class="fs-15 fw-700">{{
                $t("insights.By_Job_Tenure")
              }}</span>
            </div>
            <el-tooltip effect="dark" placement="top">
              <div class="tooltipHelpStyle" slot="content">
                <div>{{ $t("insights.A_breakdown_of_your") }}</div>
                <div class="mt-4">
                  Happiness and Risk use check-ins. Active uses people in the cohort.
                  Open Feedback shows responses to open-ended prompts.
                </div>
              </div>
              <div style="padding-left: 8px; display: flex; align-items: center">
                <img :src="getImgUrl('help_outline-16px')" />
              </div>
            </el-tooltip>
          </div>
          <div v-if="databarchart.time_window" class="tenure-period">
            <span>Week of {{ databarchart.time_window.label }}</span>
          </div>
          <div class="flex-mid barchart-style relative">
            <div v-if="databarchartLoading" class="ml-auto absolute-mid">
              <loading-even></loading-even>
            </div>
            <div v-else-if="!databarchartLoading && !Object.keys(databarchart).length
              " class="opa-06 fs-13">
              {{ $t('insights.tenure_empty_state') }}
            </div>
            <div class="pt-12" style="height: 188px; width: 100%">
              <canvas height="188px" ref="barchart"></canvas>
            </div>
          </div>
          <div
            v-if="databarchart.quality_issues && databarchart.quality_issues.length"
            class="tenure-data-warning"
          >
            Some source counts are inconsistent. Review the tenure data before relying on these rates.
          </div>
          <div v-if="tenureInsight && tenureInsight.show" class="tenure-callout flex">
            <i class="el-icon-warning-outline tenure-callout-icon"></i>
            <div class="tenure-callout-text">
              <span class="fw-700">Mid-tenure dip.</span>
              The {{ tenureInsight.label }} cohort ({{ tenureInsight.people }} people) sits at
              {{ tenureInsight.happy }}% happiness with {{ tenureInsight.risk }}% flagged at risk, above the
              {{ tenureInsight.avg }}% company average.
            </div>
          </div>
        </div>
        <div class="past-12-hours ml-16">
          <div class="fs-15 fw-700">
            {{ $t("insights.Real_time_Activity") }}
          </div>
          <div class="card rt-card">
            <div class="rt-top">
              <span class="rt-eyebrow">{{ $t("insights.PAST_12_HOURS") }}</span>
              <span class="rt-live"><span class="rt-live-dot"></span></span>
            </div>
            <div class="rt-hero">
              <div class="rt-metric">
                <div class="rt-metric-num">{{ maximumUsers }}</div>
                <div class="rt-metric-cap">{{ $t("insights.Interactions_caption") }}</div>
              </div>
              <div class="rt-spark">
                <canvas ref="pastLinechart"></canvas>
              </div>
            </div>
            <div class="rt-list-head">
              <span class="rt-eyebrow">{{ $t("insights.TOP_ACTIVITIES") }}</span>
              <span class="rt-eyebrow">{{ $t("insights.TIMES") }}</span>
            </div>
            <div class="rt-divider"></div>
            <template v-if="past12Hour && past12Hour.length">
              <div class="rt-row" v-for="(item, index) in past12Hour" :key="index + '_list_' + item.label">
                <span class="rt-row-dot"></span>
                <span class="rt-row-label">{{ item.label }}</span>
                <span class="rt-row-val">{{ item.value }}</span>
              </div>
            </template>
            <div v-else class="rt-empty">{{ $t("insights.No_activity_yet") }}</div>
          </div>
        </div>
      </div>

      <div class="mt-40">
        <div style="justify-content: flex-start">
          <div class="flex">
            <div>
              <span class="fs-15 fw-700">{{
                $t("insights.Engagement_Trend")
              }}</span>
            </div>
            <el-tooltip effect="dark" placement="top">
              <div class="tooltipHelpStyle" slot="content">
                {{ $t("insights.Engagement_Trend_Desc") }}
              </div>
              <div style="padding-left: 8px; display: flex; align-items: center">
                <img :src="getImgUrl('help_outline-16px')" />
              </div>
            </el-tooltip>
          </div>
        </div>
        <div class="grid-3-engagement mt-12">
          <div style="justify-content: flex-start">
            <div class="flex">
              <div>
                <div class="fs-12 chart-legend">
                  <div class="label">{{ $t('insights.overall_label') }}</div>
                  <div class="symbol blue"></div>
                  <div class="score">{{ (databarchartEngagementScore['company']) ? databarchartEngagementScore['company'][databarchartEngagementScore['company'].length - 1] : '' }}</div>
                  <div :class="`tag ${detectColorTotal('company')}`">{{detectTextTotal('company')}}</div>
                </div>
              </div>
            </div>
            <div class="flex-mid barchart-engagement-style relative">
              <div v-if="EngagementScoreLoading" class="ml-auto absolute-mid">
                <loading-even></loading-even>
              </div>
              <div v-else-if="!EngagementScoreLoading && databarchartEngagementScore['company'] && !Object.keys(databarchartEngagementScore['company']).length
                " class="opa-06 ml-auto absolute-mid">
                {{ $t('insights.pending_data') }}
              </div>
              <div class="" style="height: 80px; width: 100%">
                <canvas height="166px" ref="chartEngagementScoreCompany"></canvas>
              </div>
            </div>
          </div>

          <div style="justify-content: flex-start">
            <div class="flex">
              <div>
                <div class="fs-12 chart-legend">
                  <div class="label">{{ $t('insights.Managers') }}</div>
                  <div class="symbol orange"></div>
                  <div class="score">{{ (databarchartEngagementScore['manager']) ? databarchartEngagementScore['manager'][databarchartEngagementScore['manager'].length - 1] : '' }}</div>
                  <div :class="`tag ${detectColorTotal('manager')}`">{{detectTextTotal('manager')}}</div>
                </div>
              </div>
            </div>
            <div class="flex-mid barchart-engagement-style relative">
              <div v-if="EngagementScoreLoading" class="ml-auto absolute-mid">
                <loading-even></loading-even>
              </div>
              <div v-else-if="!EngagementScoreLoading && databarchartEngagementScore['manager'] && !Object.keys(databarchartEngagementScore['manager']).length
                " class="opa-06 ml-auto absolute-mid">
                {{ $t('insights.pending_data') }}
              </div>
              <div class="" style="height: 80px; width: 100%">
                <canvas height="166px" ref="chartEngagementScoreManager"></canvas>
              </div>
            </div>
          </div>

          <div style="justify-content: flex-start">
            <div class="flex">
              <div>
                <div class="fs-12 chart-legend">
                  <div class="label">{{ $t('insights.leadership_label') }}</div>
                  <div class="symbol pink"></div>
                  <div class="score">{{ (databarchartEngagementScore['leader']) ? databarchartEngagementScore['leader'][databarchartEngagementScore['leader'].length - 1]: '' }}</div>
                  <div :class="`tag ${detectColorTotal('leader')}`">{{detectTextTotal('leader')}}</div>
                </div>
              </div>
            </div>
            <div class="flex-mid barchart-engagement-style relative">
              <div v-if="EngagementScoreLoading" class="ml-auto absolute-mid">
                <loading-even></loading-even>
              </div>
              <div v-else-if="!EngagementScoreLoading && databarchartEngagementScore['leader'] && !Object.keys(databarchartEngagementScore['leader']).length
                " class="opa-06 ml-auto absolute-mid">
                {{ $t('insights.pending_data') }}
              </div>
              <div class="" style="height: 80px; width: 100%">
                <canvas height="166px" ref="chartEngagementScoreLeader"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- <div>
        <span class="fs-15 fw-700">{{
          $t("insights.Engagement_Score")
        }}</span>
      </div>
      <div class="grid-3-engagement mt-32">
      </div> -->

      <div style="justify-content: flex-end" class="mt-40">
        <div class="flex center" style="padding-bottom: 12px">
          <div>
            <span class="fs-15 fw-700">{{
              $t("insights.Key_Teams_and_Cohorts")
            }}</span>
          </div>
          <el-tooltip effect="dark" placement="top">
            <div class="tooltipHelpStyle" slot="content">
              {{ $t("insights.Highlighting_key_groups") }}
            </div>
            <div style="padding-left: 8px; display: flex; align-items: center">
              <img :src="getImgUrl('help_outline-16px')" />
            </div>
          </el-tooltip>
        </div>
        <div class="grid-5-20" v-if="datakeyGroupLoading">
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
          <div class="KeyGroupBox text-skeleton"></div>
        </div>
        <div class="grid-5-20" v-else>
          <div v-for="item in datakeygroup" :key="item.key">
            <el-tooltip effect="dark" placement="top" :disabled="item.keygroup.length < 16">
              <div class="tooltipKeyGroupStyle" 
              :class="[ blurMode ?  'blurelement': '' ]"
              slot="content">
                {{ item.keygroup }}
              </div>
              <div class="KeyGroupBox"
              :class="[ blurMode ?  'blurelement': '' ]">
                <div style="
                    padding-top: 16px;
                    padding-left: 14px;
                    position: absolute;
                  ">
                  <img v-if="item.emotion == 'bad'" :src="getImgUrlDim('ic_sad')" />
                  <img v-else-if="item.emotion == 'low_activity'" :src="getImgUrlDim('ic_low_activity')" />
                  <img v-else-if="item.emotion == 'low_feedback'" :src="getImgUrlDim('ic_low_feedback')" />
                  <img v-else :src="getImgUrlDim('ic_happy')" />
                </div>
                <div class="pt-4 pr-6" style="padding-left: 39px">
                  <span class="txtHeaderKey">{{
                    truncateTextKeyGroup(item.keygroup)
                  }}</span>
                </div>
                <div style="padding-left: 39px; margin-top: -5px">
                  <span class="txtDetailKey">{{ item.desc_text }}</span>
                </div>
              </div>
            </el-tooltip>
          </div>
        </div>
      </div>

      <div class="grid-2-second mt-44">
        <div>
          <div class="flex-center">
            <div class="fs-18 fw-700">{{ $t("insights.TONE_ANALYZER") }}</div>
            <!-- <span class="icon-beta">{{ $t("insights.BETA") }}</span> -->
          </div>

          <!-- Tone balance: a clean proportion "mood meter". The exact split
               lives on hover so it doesn't compete with the per-tone list
               below (which is shares of all feedback, not of this subset). -->
          <div v-if="!toneLoadingData && toneValence" class="tone-valence">
            <el-tooltip placement="top" effect="light" popper-class="hv-tip">
              <div slot="content">
                Of this week's top tones: {{ toneValence.posPct }}% positive, {{ toneValence.neuPct }}% neutral, {{ toneValence.negPct }}% negative.
              </div>
              <div class="tone-valence-bar">
                <div v-if="toneValence.posPct > 0" class="tvseg tv-pos" :style="`width:${toneValence.posPct}%`"></div>
                <div v-if="toneValence.neuPct > 0" class="tvseg tv-neu" :style="`width:${toneValence.neuPct}%`"></div>
                <div v-if="toneValence.negPct > 0" class="tvseg tv-neg" :style="`width:${toneValence.negPct}%`"></div>
              </div>
            </el-tooltip>
            <div class="tone-valence-meta">
              <span class="tone-valence-mood" :class="toneValence.improving ? 'good' : 'bad'">
                <i :class="toneValence.improving ? 'el-icon-top' : 'el-icon-bottom'"></i>
                {{ toneValence.improving ? 'Mostly constructive' : 'Watch — negative tone rising' }}
              </span>
              <span class="tone-valence-legend">
                <span class="tvdot tv-pos"></span>Positive
                <span class="tvdot tv-neu"></span>Neutral
                <span class="tvdot tv-neg"></span>Negative
              </span>
            </div>
          </div>

          <div v-if="!toneLoadingData">
            <div class="tone-body flex" v-for="(data, index) of toneData" :key="index + data.toneId">
              <div class="tone-rank">{{ data.rankingNow }}</div>
              <span class="tone-valence-dot" :class="'v-' + toneValenceOf(data.toneId)"></span>
              <el-tooltip placement="top" effect="light" popper-class="hv-tip">
                <div slot="content">
                  {{ data.details }}
                </div>
                <div class="tone-name ellipsis">{{ data.emojiText }}</div>
              </el-tooltip>
              <div class="percentage-bar ellipsis">
                <el-progress :percentage="toneBarPct(data.percentage)" :show-text="false" :color="toneBarColor(data.toneId)"></el-progress>
              </div>
              <div class="percentage-number opa-09">
                {{ data.percentage }}%
              </div>
              <el-tooltip placement="top" effect="light" popper-class="hv-tip">
                <div slot="content">
                  {{ toneValenceOf(data.toneId) === 'positive' ? 'A constructive tone — green when it is rising, red when it is fading.' : (toneValenceOf(data.toneId) === 'negative' ? 'A warning tone — green when it is easing, red when it is climbing.' : 'A neutral tone.') }}
                </div>
                <div class="percentage-change-number" :class="toneChangeClass(data)">
                  <i v-if="data.percentageChange > 0" class="el-icon-top tone-change-arrow"></i>
                  <i v-else-if="data.percentageChange < 0" class="el-icon-bottom tone-change-arrow"></i>
                  {{ data.percentageChange === 0 ? '—' : Math.abs(data.percentageChange) + '%' }}
                </div>
              </el-tooltip>
            </div>
          </div>
          <div v-else>
            <div class="tone-body text-skeleton" style="height: 20px; width: 320px"></div>
            <div class="tone-body text-skeleton" style="height: 20px; width: 320px"></div>
            <div class="tone-body text-skeleton" style="height: 20px; width: 320px"></div>
            <div class="tone-body text-skeleton" style="height: 20px; width: 220px"></div>
          </div>

          <!-- Communication Health: gibberish (quality) and toxic (safety)
               read as two compact status rows. The full per-team breakdown and
               recommended actions open in a detail dialog — this view's own
               click-to-detail pattern — rather than being trapped inside a
               hover tooltip you cannot comfortably interact with. -->
          <div v-if="!toneLoadingData" class="mt-32">
            <div class="ch-head">
              <span class="fs-14 fw-700 fc-2F2F2F">{{ $t('insights.communication_health') }}</span>
              <el-tooltip placement="top" effect="light" popper-class="hv-tip">
                <div slot="content">Quality and safety signals in this week's written feedback.</div>
                <i class="el-icon-question ch-head-help"></i>
              </el-tooltip>
            </div>

            <div class="ch-strip">
              <!-- Gibberish = feedback quality -->
              <el-tooltip placement="top" effect="light" popper-class="hv-tip">
                <div slot="content">
                  <template v-if="(gibbrishTone.count || 0) > 0">{{ gibbrishTone.count }} unclear {{ (gibbrishTone.count || 0) === 1 ? 'response' : 'responses' }} across {{ Object.keys(gibbrishByTeam).length }} {{ Object.keys(gibbrishByTeam).length === 1 ? 'team' : 'teams' }}. Click to view the breakdown.</template>
                  <template v-else>No gibberish this week. Every response was clear enough to read.</template>
                </div>
                <div class="ch-row" :class="[(gibbrishTone.count || 0) > 0 ? 'is-warn' : 'is-ok', (gibbrishTone.count || 0) > 0 ? 'is-clickable' : '']" @click="(gibbrishTone.count || 0) > 0 && openCommHealth('gibberish')">
                  <span class="ch-status"><i :class="(gibbrishTone.count || 0) > 0 ? 'el-icon-warning-outline' : 'el-icon-circle-check'"></i></span>
                  <span class="ch-label">
                    <span class="ch-label-title">Gibberish</span>
                    <span class="ch-label-sub">Unclear or low-effort feedback</span>
                  </span>
                  <span class="ch-meta">
                    <template v-if="(gibbrishTone.count || 0) > 0">
                      <span v-if="(gibbrishTone.percentageChange || 0) !== 0" class="ch-delta" :class="(gibbrishTone.percentageChange || 0) < 0 ? 'is-good' : 'is-bad'">
                        <i :class="(gibbrishTone.percentageChange || 0) < 0 ? 'el-icon-bottom' : 'el-icon-top'"></i>{{ Math.abs(gibbrishTone.percentageChange || 0) }}%
                      </span>
                      <span class="ch-count">{{ gibbrishTone.count }}</span>
                      <i class="el-icon-arrow-right ch-go"></i>
                    </template>
                    <span v-else class="ch-clear">None this week</span>
                  </span>
                </div>
              </el-tooltip>

              <!-- Toxic = communication safety -->
              <el-tooltip placement="top" effect="light" popper-class="hv-tip">
                <div slot="content">
                  <template v-if="(toxicTone.count || 0) > 0">{{ toxicTone.count }} harmful {{ (toxicTone.count || 0) === 1 ? 'response' : 'responses' }} across {{ Object.keys(toxicByTeam).length }} {{ Object.keys(toxicByTeam).length === 1 ? 'team' : 'teams' }}. Click to review and act.</template>
                  <template v-else>No toxic feedback this week. A healthy communication environment.</template>
                </div>
                <div class="ch-row" :class="[(toxicTone.count || 0) > 0 ? 'is-danger' : 'is-ok', (toxicTone.count || 0) > 0 ? 'is-clickable' : '']" @click="(toxicTone.count || 0) > 0 && openCommHealth('toxic')">
                  <span class="ch-status"><i :class="(toxicTone.count || 0) > 0 ? 'el-icon-warning' : 'el-icon-circle-check'"></i></span>
                  <span class="ch-label">
                    <span class="ch-label-title">Toxic</span>
                    <span class="ch-label-sub">Harmful or disrespectful feedback</span>
                  </span>
                  <span class="ch-meta">
                    <template v-if="(toxicTone.count || 0) > 0">
                      <span class="ch-flag">Review now</span>
                      <span class="ch-count">{{ toxicTone.count }}</span>
                      <i class="el-icon-arrow-right ch-go"></i>
                    </template>
                    <span v-else class="ch-clear">None this week</span>
                  </span>
                </div>
              </el-tooltip>
            </div>
          </div>

          <!-- Communication Health detail: full per-team breakdown + actions -->
          <el-dialog :visible.sync="commHealthDialog" custom-class="comm-health-dialog" width="460px">
            <div v-if="commHealthActive" class="ch-dlg">
              <div class="ch-dlg-head">
                <div class="qd-eyebrow">{{ $t('insights.communication_health') }}</div>
                <div class="ch-dlg-title-row">
                  <span class="ch-dlg-title">{{ commHealthActive.title }}</span>
                  <span class="ch-dlg-count" :class="commHealthActive.kindClass">{{ commHealthActive.count }} detected</span>
                  <span v-if="commHealthActive.change !== 0" class="ch-delta" :class="commHealthActive.change < 0 ? 'is-good' : 'is-bad'">
                    <i :class="commHealthActive.change < 0 ? 'el-icon-bottom' : 'el-icon-top'"></i>{{ Math.abs(commHealthActive.change) }}% vs last week
                  </span>
                </div>
                <div class="ch-dlg-desc">{{ commHealthActive.desc }}</div>
              </div>

              <div class="ch-dlg-block" v-if="commHealthActive.teams.length">
                <div class="ch-dlg-block-head">
                  <span class="qd-eyebrow">Team breakdown</span>
                  <span class="ch-dlg-teamcount">{{ commHealthActive.teams.length }} {{ commHealthActive.teams.length === 1 ? 'team' : 'teams' }}</span>
                </div>
                <div class="ch-dlg-rows thin-scrollbar">
                  <div v-for="(t, i) in commHealthActive.teams" :key="t.team" class="ch-dlg-row">
                    <span class="ch-dlg-rank">{{ i + 1 }}</span>
                    <span class="ch-dlg-team" :class="blurMode ? 'blurelement' : ''">{{ t.team }}</span>
                    <span class="ch-dlg-bar"><span class="ch-dlg-fill" :class="commHealthActive.kindClass" :style="{ width: barW(t.count, commHealthActive.maxCount) }"></span></span>
                    <span class="ch-dlg-chip" :class="commHealthActive.kindClass">{{ t.count }}</span>
                  </div>
                </div>
              </div>

              <div class="ch-dlg-block">
                <div class="qd-eyebrow ch-dlg-actions-head">Recommended actions</div>
                <ul class="ch-dlg-actions">
                  <li v-for="(a, i) in commHealthActive.actions" :key="i">{{ a }}</li>
                </ul>
              </div>
            </div>
            <span slot="footer">
              <el-button @click="commHealthDialog = false" class="buttonClose btn-gray-outline">{{ $t('insights.Close') }}</el-button>
            </span>
          </el-dialog>

        </div>

        
        <div>
          <div class="tp-header">
            <div>
              <span class="fs-18 fw-700">{{
                $t("insights.TALKING_POINTS")
              }}</span>
              <el-button type="text" class="txtButton" @click="showList">{{
                $t("insights.show_timeline")
              }}</el-button>
            </div>
            <div class="tp-legend">
              <span><span class="tp-leg-mark s-pos"></span>{{ $t("insights.Positive") }}</span>
              <span><span class="tp-leg-mark s-neu"></span>{{ $t("insights.Neutral") }}</span>
              <span><span class="tp-leg-mark s-neg"></span>{{ $t("insights.Critical") }}</span>
            </div>
          </div>


          <!-- dialog talking points -->
          <el-dialog :visible.sync="dialogVisible" @close="destroySentimentChart">
            <span slot="title">
              <div class="headerModalStyle flex-center">
                <div class="flex-1">
                  <span class="fs-20 fw-800 fc-2F2F2F">{{
                    $t("insights.TALKING_POINTS")
                  }}</span>
                </div>
                <div>
                  <el-tag style="margin: 0 8px 0 0 !important"
                    :class="['talking-points mt-2 mb-6 GreenBoxStyle', activeSentiments.positive ? 'legend-tag-active' : 'legend-tag-inactive']"
                    @click.native="toggleSentiment('positive')">{{
                    $t("insights.Positive") }}</el-tag>
                  <el-tag style="margin: 0 8px 0 0 !important"
                    :class="['talking-points mt-2 mb-6 GreyBoxStyle', activeSentiments.neutral ? 'legend-tag-active' : 'legend-tag-inactive']"
                    @click.native="toggleSentiment('neutral')">{{
                    $t("insights.Neutral") }}</el-tag>
                  <el-tag style="margin: 0 8px 0 0 !important"
                    :class="['talking-points mt-2 mb-6 RedBoxStyle', activeSentiments.negative ? 'legend-tag-active' : 'legend-tag-inactive']"
                    @click.native="toggleSentiment('negative')">{{
                    $t("insights.Critical") }}</el-tag>
                </div>
              </div>
            </span>
            <!-- Sentiment Chart with Viewport Navigator -->
            <div class="sentiment-chart-container" v-if="sentimentChartData && !loadingTimeLineTalkingPoints">
              <div class="sentiment-chart-wrapper" ref="sentimentChartWrapper">
                <canvas ref="sentimentChart" style="height: 130px; width: 100%;"></canvas>
                <div class="viewport-brush" ref="viewportBrush"
                  @mousedown.prevent="startViewportDrag($event)">
                  <div class="viewport-brush-handle left"></div>
                  <div class="viewport-brush-handle right"></div>
                </div>
              </div>
            </div>
            <div class="scroll-style thin-scrollbar" ref="scrollContainer">
              <div class="center mt-40 pt-40 mb-10 pb-10" v-if="loadingTimeLineTalkingPoints">
                <loading-even></loading-even>
              </div>
              <div class="mx-52 mb-30" v-for="(item, index) in talking_points_monthly" :key="index" :ref="'month-' + index">
                <div style="display: flex; justify-content: center" class="mb-8 mt-20">
                  <span class="txtMonthlyStyle">{{ setMonthly(index) }}</span>
                </div>
                <!-- Sentiment Breakdown Bar -->
                <div class="sentiment-breakdown" v-if="monthSentimentStats[index]">
                  <div class="sentiment-bar">
                    <div class="sentiment-bar-segment positive"
                      :style="{ width: (monthSentimentStats[index].total ? (monthSentimentStats[index].positive / monthSentimentStats[index].total * 100) : 0) + '%' }"></div>
                    <div class="sentiment-bar-segment neutral"
                      :style="{ width: (monthSentimentStats[index].total ? (monthSentimentStats[index].neutral / monthSentimentStats[index].total * 100) : 0) + '%' }"></div>
                    <div class="sentiment-bar-segment negative"
                      :style="{ width: (monthSentimentStats[index].total ? (monthSentimentStats[index].negative / monthSentimentStats[index].total * 100) : 0) + '%' }"></div>
                  </div>
                  <div class="sentiment-summary">
                    {{ monthSentimentStats[index].positive }} {{ $t("insights.Positive") }} &middot; {{ monthSentimentStats[index].neutral }} {{ $t("insights.Neutral") }} &middot; {{ monthSentimentStats[index].negative }} {{ $t("insights.Critical") }}
                  </div>
                </div>
                <!-- Lazy-rendered tag content -->
                <div v-if="visibleMonthKeys[index]" class="month-tags-content">
                  <div v-if="index < '2024-12'" class="flex" style="flex-wrap: wrap">
                    <div :class="['talkingPointsBox flex', { 'tag-enter': !animatedMonthKeys[index], 'tag-filtered-out': !activeSentiments[getSentimentType(_value, index)] }]" v-for="(_value, i_val) in item.slice(0, 20)" :key="_value._id"
                      :style="{ 'animation-delay': (i_val * 0.03) + 's' }">
                      <el-tooltip v-if="_value.short_answer.length > maxLength" effect="dark" placement="top">
                        <div class="tooltipStyle" slot="content">
                          {{ _value.short_answer }}
                        </div>
                        <el-tag :class="`talking-points ${_value.sentiment > 0
                          ? 'GreenBoxStyle'
                          : _value.sentiment < 0
                            ? 'RedBoxStyle'
                            : 'GreyBoxStyle'
                          }`" v-bind:style="'margin-bottom: 8px;' +
      $utils('fontStyle', _value.short_answer)
      ">{{ truncateText(_value.short_answer) }}</el-tag>
                      </el-tooltip>
                      <div v-else>
                        <el-tag :class="`talking-points ${_value.sentiment > 0
                          ? 'GreenBoxStyle'
                          : _value.sentiment < 0
                            ? 'RedBoxStyle'
                            : 'GreyBoxStyle'
                          }`" v-bind:style="'margin-bottom: 8px;' +
      $utils('fontStyle', _value.short_answer)
      ">{{ truncateText(_value.short_answer) }}</el-tag>
                      </div>
                    </div>
                  </div>
                  <div v-else class="flex" style="flex-wrap: wrap">
                    <div :class="['talkingPointsBox flex', { 'tag-enter': !animatedMonthKeys[index], 'tag-filtered-out': !activeSentiments[getSentimentType(_value, index)] }]" v-for="(_value,i_array) in item.slice(0, 20)" :key="index+'_'+i_array"
                      :style="{ 'animation-delay': (i_array * 0.03) + 's' }">
                      <el-tooltip effect="light" placement="top" class="custom-tooltip" :disabled="user.user_type == 'leadership'?true:false">
                        <div class="tooltipStyle" slot="content">
                          <span class="fs-13 fw-700">{{ _value.point.title }}</span>
                          <div v-if="_value.point && _value.point.specific_examples">
                            <div class="tooltip-items" v-for="(item_specific,index_specific) in _value.point.specific_examples" :key="index+'_'+index_specific">
                              <div class="tooltip-item">
                                <div class="oi-circle">
                                  <span class="fs-11">{{ index_specific+1 }}</span>
                                </div>
                                <p>
                                  {{ item_specific }}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <el-tag :class="`talking-points ${_value.sentiment == 'positive'
                          ? 'GreenBoxStyle'
                          : _value.sentiment == 'negative'
                            ? 'RedBoxStyle'
                            : 'GreyBoxStyle'
                          }`" v-bind:style="'margin-bottom: 8px;' +
                          $utils('fontStyle', _value.point.title)
                          ">{{ truncateText(_value.point.title) }}</el-tag>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
                <div v-else class="month-tags-placeholder" :style="{ height: (monthContentHeights[index] || 160) + 'px' }"></div>
              </div>
            </div>
            <span slot="footer" class="dialog-footer" style="margin-right: 10px">
              <el-button @click="dialogVisible = false" class="buttonClose btn-gray-outline">{{ $t("insights.Close")
              }}</el-button>
            </span>
          </el-dialog>




          <!-- Sentiment-led themes. The title is the scannable signal; the
               supporting evidence (specific examples) is a deliberate drill-in,
               so it opens in a focused detail modal on click rather than
               flashing a cramped tooltip on every hover. Rails match the tone
               valence colours. -->
          <div class="tp-list thin-scrollbar">
            <div
              v-for="(item, index) in talking_points_daily.slice(0, 20)" :key="index"
              class="tp-item"
              :class="['tp-' + (item.sentiment == 'positive' ? 'pos' : (item.sentiment == 'negative' ? 'neg' : 'neu')), tpHasDetails(item) ? 'tp-clickable' : '']"
              @click="tpHasDetails(item) && openTalkingPoint(item)">
              <span class="tp-rail"></span>
              <span class="tp-text" :class="blurMode ? 'blurelement' : ''">{{ item.point.title }}</span>
              <span v-if="tpHasDetails(item)" class="tp-evidence">
                <span class="tp-ev-count">{{ item.point.specific_examples.length }}</span>
                <i class="el-icon-arrow-right tp-ev-go"></i>
              </span>
            </div>
          </div>

          <!-- Talking point detail: the full theme + the specific examples
               behind it, surfaced on click instead of a hover tooltip. -->
          <el-dialog :visible.sync="talkingPointDialog" custom-class="tp-dialog" width="480px">
            <div v-if="talkingPointActive" class="tpd">
              <div class="tpd-head">
                <div class="tpd-eyebrow-row">
                  <span class="qd-eyebrow">{{ $t('insights.TALKING_POINTS') }}</span>
                  <span class="tpd-sentiment" :class="'s-' + talkingPointActive.sentimentKey">
                    <span class="tpd-sent-dot"></span>{{ talkingPointActive.sentimentLabel }}
                  </span>
                </div>
                <div class="tpd-title" :class="blurMode ? 'blurelement' : ''">{{ talkingPointActive.title }}</div>
              </div>

              <div class="tpd-block" v-if="talkingPointActive.examples.length">
                <div class="tpd-block-head">
                  <span class="qd-eyebrow">What people said</span>
                  <span class="tpd-count">{{ talkingPointActive.examples.length }}</span>
                </div>
                <div class="tpd-examples thin-scrollbar">
                  <div v-for="(ex, i) in talkingPointActive.examples" :key="i" class="tpd-ex" :class="blurMode ? 'blurelement' : ''">
                    <span class="tpd-ex-num" :class="'s-' + talkingPointActive.sentimentKey">{{ i + 1 }}</span>
                    <p class="tpd-ex-text">{{ ex }}</p>
                  </div>
                </div>
              </div>
            </div>
            <span slot="footer">
              <el-button @click="talkingPointDialog = false" class="buttonClose btn-gray-outline">{{ $t('insights.Close') }}</el-button>
            </span>
          </el-dialog>






        </div>

        
      </div>


      <div class="flex-center mt-40">
        <div class="flex-1">
          <span class="fs-18 fw-700">{{
            $t("insights.ENGAGEMENT_BREAKDOWN_BY_TEAM")
          }}</span>
        </div>
        <div style="padding-right: 12px">
          <span class="fc-000000 fs-14">{{ $t("insights.Sort_by") }}</span>
        </div>
        <div class="showOption">
          <el-menu class="el-menu-demo menuOptionStyle" mode="horizontal" style="width: 220px">
            <el-submenu index="0" style="width: 220px">
              <template slot="title">
                <span class="fc-4A4A4A fs-16 ml-16" style="width: 160px; display: inline-block" id="sortby">{{
                  $t("insights.Lowest_Success_Score") }}</span>
              </template>
              <el-menu-item index="1" class="ml-16 fs-16" @click="sortBy('mostred')" style="width: 220px">{{
                $t("insights.Most_teams_red") }}</el-menu-item>
              <el-menu-item index="2" class="ml-16 fs-16" @click="sortBy('biggestdrop')" style="width: 220px">{{
                $t("insights.Biggest_drop") }}</el-menu-item>
              <el-menu-item index="3" class="ml-16 fs-16" @click="sortBy('lowest')" style="width: 220px">{{
                $t("insights.Lowest_Success_Score") }}</el-menu-item>
              <el-menu-item index="4" class="ml-16 fs-16" @click="sortBy('highest')" style="width: 220px">{{
                $t("insights.Highest_Success_Score") }}</el-menu-item>
            </el-submenu>
          </el-menu>
        </div>
      </div>
      <div class="divTable">
        <el-table :data="questions" :default-sort="{ prop: 'name', order: 'ascending' }" style="width: 100%"
          empty-text="Loading..." :key="questionsTableKey" @expand-change="onDimensionExpand">
          <el-table-column type="expand" width="40">
            <template slot-scope="scope">
              <div class="dim-teams-panel">
                <div class="dim-teams-head">
                  <span class="qd-eyebrow">{{ $t('insights.Critical_teams') }}</span>
                  <span class="dim-teams-count" v-if="criticalTeamsFor(scope.row.quest_id).length > 0">· {{ criticalTeamsFor(scope.row.quest_id).length }}</span>
                </div>
                <div v-if="engagementTeamBreakdownLoading && criticalTeamsFor(scope.row.quest_id).length === 0"
                  class="rag-caption dim-teams-empty">
                  {{ $t('insights.Loading_teams') }}
                </div>
                <div v-else-if="criticalTeamsFor(scope.row.quest_id).length === 0" class="rag-caption dim-teams-empty">
                  {{ $t('insights.No_critical_teams') }}
                </div>
                <ul v-else class="dim-teams-list">
                  <li v-for="t in criticalTeamsFor(scope.row.quest_id)" :key="t.teamId" class="dim-team-row">
                    <span class="dim-team-dot" :class="bandDotClass(t.band)"></span>
                    <span class="dim-team-name">{{ t.name }}</span>
                    <span class="dim-team-total" v-if="t.total">{{ t.total }} {{ $t('insights.responses') }}</span>
                    <span class="qd-score-chip" :class="bandChipClass(t.band)">{{ t.score != null ? t.score : '-' }}</span>
                  </li>
                </ul>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('insights.Dimension')" width="90" align="center">
            <template slot-scope="scope">
              <div class="flex-center">
                <img v-if="scope.row.dimpic" v-lazy="getImgUrlDim(scope.row.dimpic)"
                  class="image-skeleton image-profile-60" />
                <div v-else class="divCurclePic"></div>
              </div>
            </template>
          </el-table-column>
          <el-table-column>
            <template slot-scope="scope">
              <div class="flex-column" style="cursor: pointer" @click="DimensionClicked(scope.row)">
                <span class="fc-4A4A4A fs-16 fw-700">{{ scope.row.name }}</span>
                <span class="txtDetailTable">{{
                  questions_name[scope.row.quest_id]
                }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('insights.Status_no_teams')" width="240" align="left">
            <template slot-scope="scope">
              <div v-if="teamTotal(scope.row) > 0" class="rag-cell">
                <el-tooltip placement="top" effect="light" popper-class="hv-tip">
                  <div slot="content">
                    {{ scope.row.status.green }} healthy · {{ scope.row.status.yellow }} watch · {{ scope.row.status.red }} critical
                  </div>
                  <div class="rag-bar">
                    <span class="rag-seg score-band-high" v-if="scope.row.status.green > 0"
                      :style="`width:${ragWidth(scope.row, 'green')}%`"></span>
                    <span class="rag-seg score-band-moderate" v-if="scope.row.status.yellow > 0"
                      :style="`width:${ragWidth(scope.row, 'yellow')}%`"></span>
                    <span class="rag-seg score-band-low" v-if="scope.row.status.red > 0"
                      :style="`width:${ragWidth(scope.row, 'red')}%`"></span>
                  </div>
                </el-tooltip>
                <div class="rag-caption">
                  <span class="rag-emph" :class="belowHealthy(scope.row) > 0 ? 'rag-emph-warn' : ''">{{ belowHealthy(scope.row) }} of {{ teamTotal(scope.row) }}</span>
                  {{ $t('insights.teams_below_healthy') }}
                </div>
              </div>
              <div v-else class="rag-caption">{{ $t('insights.no_team_data') }}</div>
            </template>
          </el-table-column>
          <el-table-column prop :label="$t('insights.Success_Score')" width="180" align="right">
            <template slot-scope="scope">
              <div class="success-score-cell">
                <span class="qd-score-chip" :class="scoreBand(scope.row.success)">{{ scope.row.success }}</span>
                <span class="ssc-delta" :class="scope.row.compare > 0 ? 'green' : (scope.row.compare < 0 ? 'red' : 'grey')">
                  {{ scope.row.compare >= 0 ? '+' : '' }}{{ scope.row.compare }}%
                </span>
                <span class="ssc-trend">
                  <svg v-if="getMiniTrend(scope.row.quest_id).length >= 2" :width="miniTrendWidth" :height="miniTrendHeight" viewBox="0 0 40 16">
                    <polyline
                      :points="computeSparkline(getMiniTrend(scope.row.quest_id))"
                      fill="none"
                      :stroke="sparkColor(getMiniTrend(scope.row.quest_id))"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <el-tooltip v-else effect="light" placement="top" popper-class="hv-tip" content="Not enough history for a trend yet">
                    <span class="ssc-trend-empty"></span>
                  </el-tooltip>
                </span>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <el-dialog :visible.sync="dialogDimensionVisible">
        <span slot="title">
          <div class="headerModalStyle flex-center">
            <div class="flex-1">
              <span class="fs-20 fw-800 fc-2F2F2F">{{
                $t("insights.DIMENSION_DETAILS")
              }}</span>
            </div>
          </div>
        </span>
        <div class="scroll-style scrollingStyle2 thin-scrollbar">
          <div class="mt-2 mx-20 mb-32">
            <div class="flex-center-1">
              <img v-if="dimensionData.dimpic" :src="getImgUrlDim(dimensionData.dimpic)"
                class="image-skeleton image-profile-60" />
              <div v-else class="divCurclePic"></div>
              <div class="ml-21 mt-10">
                <div>
                  <span class="fc-000000 fs-18 fw-700">{{
                    dimensionData.name
                  }}</span>
                </div>
                <div class="mt-4">
                  <span class="txtDimensionDesc">{{
                    questions_name[dimensionData.quest_id]
                  }}</span>
                </div>
              </div>
            </div>
            <div class="flex mt-15" style="height: 300px">
              <canvas ref="chartTalkingPoints" class="chartStyle"></canvas>
            </div>
            <div class="flex-1 mt-24 mb-16">
              <span class="fs-18 fc-000000 fw-700">
                {{ $t("insights.TALKING_POINTS") }}
              </span>
            </div>
            <div style="margin-top: 8px; max-height: 164px; flex-wrap: wrap" class="flex">
              <div class="talkingPointsBox" v-for="(item,index) in talking_points_30d.slice(0, 20)" :key="index">
                <el-tooltip effect="light" placement="top" class="custom-tooltip">
                  <div class="tooltipStyle" slot="content">
                    <span class="fs-13 fw-700">{{ item.point.title }}</span>
                    <div v-if="item.point && item.point.specific_examples">
                      <div class="tooltip-items" v-for="(item_specific,index_specific) in item.point.specific_examples" :key="index+'_'+index_specific">
                        <div class="tooltip-item">
                          <div class="oi-circle">
                            <span class="fs-11">{{ index_specific+1 }}</span>
                          </div>
                          <p>
                            {{ item_specific }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <el-tag :class="`talking-points ${item.sentiment == 'positive'
                    ? 'GreenBoxStyle'
                    : item.sentiment == 'negative'
                      ? 'RedBoxStyle'
                      : 'GreyBoxStyle'
                    }`" v-bind:style="'margin-bottom: 8px;' +
                      $utils('fontStyle', item.point.title)
                      ">{{ truncateText(item.point.title) }}</el-tag>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>
        <span slot="footer" class="dialog-footer" style="margin-right: 10px">
          <el-button @click="dialogDimensionVisible = false" class="buttonClose btn-gray-outline">{{ $t("insights.Close")
          }}</el-button>
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
Chart.defaults.font.family = "Lato";
import ChartDataLabels from "chartjs-plugin-datalabels";
import format from "date-fns/format";
import { Result } from "element-ui";
import { DASHBOARD_ACTIVITY_REFRESH_MS, DASHBOARD_ADOPTION_REFRESH_MS } from "@/constants/liveUpdateSettings";
import { resolveGoalAlignmentDisplay } from "@/store/utils/goalAlignment";

export default {
  name: "dashboard",
  components: {
    loadingEven: require("../even/loading").default,
  },
  async created() {
    // Initialize dashboard data if user is already available
    // Otherwise, the watcher will handle it when user loads
    if (this.user) {
      await this.initializeDashboard();
    }
  },
  mounted() {
    // Listen for language changes
    this.$root.$on('languageChanged', this.handleLanguageChange);
    document.addEventListener('visibilitychange', this.handleDashboardVisibilityChange);
  },
  props: {
    tabActive: String,
  },
  computed: {
    user() {
      return this.$store.getters["authen/user"];
    },
    dimension() {
      return this.$store.getters["insights/dimension"];
    },
    activeuser() {
      return this.$store.getters["dashboard/activeuser"];
    },
    happiness() {
      return this.$store.getters["dashboard/happiness"];
    },
    feedback() {
      return this.$store.getters["dashboard/feedback"];
    },
    dateshow() {
      return this.$store.getters["dashboard/dateshow"];
    },
    questions() {
      return this.$store.getters["dashboard/questions"];
    },
    questions_name() {
      return this.$store.getters["dashboard/questions_name"];
    },
    talking_points_daily() {
      return this.$store.getters["dashboard/talking_points_daily"];
    },
    talking_points_monthly() {
      this.talkingPointsTimeLineCallAPI = ++this.talkingPointsTimeLineCallAPI;
      return this.$store.getters["dashboard/talking_points_monthly"];
    },
    talking_points_30d() {
      return this.$store.getters["dashboard/talking_points_30d"];
    },
    datachart() {
      return this.$store.getters["dashboard/datachart"];
    },
    databarchart() {
      return this.$store.getters["dashboard/databarchart"];
    },
    databarchartLoading() {
      return this.$store.getters["dashboard/datachartloadingStatus"];
    },
    databarchartEngagementScore() {
      return this.$store.getters["dashboard/databarchartEngagementScore"];
    },
    EngagementScoreLoading() {
      return this.$store.getters["dashboard/EngagementScoreLoadingStatus"];
    },
    datakeygroup() {
      return this.$store.getters["dashboard/datakeygroup"];
    },
    datakeyGroupLoading() {
      return this.$store.getters["dashboard/datakeygrouploadingStatus"];
    },
    opt_month_name() {
      return this.$store.getters["dashboard/opt_month_name"];
    },
    loadingTimeLineTalkingPoints() {
      if (
        Object.keys(this.talking_points_monthly).length === 0 &&
        this.talkingPointsTimeLineCallAPI < 2
      ) {
        return true;
      } else {
        return false;
      }
    },
    sentimentChartData() {
      const monthly = this.talking_points_monthly;
      if (!monthly || Object.keys(monthly).length === 0) return null;
      const sortedKeys = Object.keys(monthly).sort();
      const labels = [];
      const positive = [];
      const neutral = [];
      const negative = [];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      sortedKeys.forEach(key => {
        const split = key.split('-');
        const monthIdx = parseInt(split[1]) - 1;
        const year = split[0].slice(2);
        const monthLabel = (monthIdx >= 0 && monthIdx < 12) ? monthNames[monthIdx] : '?';
        labels.push(monthLabel + ' \'' + year);
        let pos = 0, neu = 0, neg = 0;
        const items = monthly[key] || [];
        items.forEach(item => {
          const type = this.getSentimentType(item, key);
          if (type === 'positive') pos++;
          else if (type === 'negative') neg++;
          else neu++;
        });
        positive.push(pos);
        neutral.push(neu);
        negative.push(neg);
      });
      return { labels, positive, neutral, negative, keys: sortedKeys };
    },
    monthSentimentStats() {
      const monthly = this.talking_points_monthly;
      if (!monthly) return {};
      const stats = {};
      Object.keys(monthly).forEach(key => {
        let pos = 0, neu = 0, neg = 0;
        const items = monthly[key] || [];
        items.forEach(item => {
          const type = this.getSentimentType(item, key);
          if (type === 'positive') pos++;
          else if (type === 'negative') neg++;
          else neu++;
        });
        stats[key] = { positive: pos, neutral: neu, negative: neg, total: pos + neu + neg };
      });
      return stats;
    },
    newLabels() {
      return this.$store.getters["dashboard/newLabels"];
    },
    toneData() {
      return this.$store.getters["dashboard/toneAnalyse"];
    },
    toneLoadingData() {
      return this.$store.getters["dashboard/toneloadingStatus"];
    },
    gibbrishTone() {
      return this.$store.getters["dashboard/gibbrishTone"];
    },
    toxicTone() {
      return this.$store.getters["dashboard/toxicTone"];
    },
    gibbrishByTeam() {
      return this.$store.getters["dashboard/gibbrishByTeam"];
    },
    toxicByTeam() {
      return this.$store.getters["dashboard/toxicByTeam"];
    },
    sortedGibbrishByTeam() {
      const teams = this.gibbrishByTeam;
      // Convert object to array and sort by count (descending)
      return Object.entries(teams)
        .sort(([, a], [, b]) => b - a)
        .reduce((acc, [team, count]) => {
          acc[team] = count;
          return acc;
        }, {});
    },
    sortedToxicByTeam() {
      const teams = this.toxicByTeam;
      // Convert object to array and sort by count (descending)
      return Object.entries(teams)
        .sort(([, a], [, b]) => b - a)
        .reduce((acc, [team, count]) => {
          acc[team] = count;
          return acc;
        }, {});
    },
    // Shapes the data behind the Communication Health detail dialog for the
    // currently-opened metric (gibberish = quality, toxic = safety). All from
    // already-loaded state — no extra Firestore reads.
    commHealthActive() {
      const type = this.commHealthType;
      if (!type) return null;
      const isToxic = type === 'toxic';
      const tone = isToxic ? this.toxicTone : this.gibbrishTone;
      const byTeam = isToxic ? this.sortedToxicByTeam : this.sortedGibbrishByTeam;
      const teams = Object.entries(byTeam).map(([team, count]) => ({ team, count }));
      const maxCount = teams.reduce((m, t) => Math.max(m, t.count), 0) || 1;
      return {
        type,
        kindClass: isToxic ? 'is-danger' : 'is-warn',
        title: isToxic ? 'Toxic feedback' : 'Gibberish feedback',
        desc: isToxic
          ? 'Harmful, disrespectful, or hostile responses. Even a few are worth a quick review.'
          : 'Unclear, off-topic, or low-effort responses that add noise to your results.',
        count: tone.count || 0,
        change: tone.percentageChange || 0,
        teams,
        maxCount,
        actions: isToxic
          ? [
              'Review the flagged feedback directly with the affected teams',
              'Prioritise support and a check-in for those teams',
              'Reinforce respectful-feedback guidelines',
              'Follow up with the relevant managers',
            ]
          : [
              'Encourage managers to model specific, useful feedback',
              'Consider simplifying or rewording survey questions',
            ],
      };
    },
    // Shapes the clicked talking point for its detail modal: the theme title,
    // its sentiment (key + localized label), and the supporting examples.
    talkingPointActive() {
      const item = this.tpActiveItem;
      if (!item) return null;
      const s = item.sentiment;
      const key = s === 'positive' ? 'pos' : (s === 'negative' ? 'neg' : 'neu');
      const label = s === 'positive'
        ? this.$t('insights.Positive')
        : (s === 'negative' ? this.$t('insights.Critical') : this.$t('insights.Neutral'));
      const examples = (item.point && Array.isArray(item.point.specific_examples))
        ? item.point.specific_examples
        : [];
      return {
        title: item.point ? item.point.title : '',
        sentimentKey: key,
        sentimentLabel: label,
        examples,
      };
    },
    problemFeedbackDetails() {
      return this.$store.getters["dashboard/problemFeedbackDetails"];
    },
    // Surfaces the cohort whose risk is both elevated and well above the
    // company average, so the tenure chart names its own concern instead of
    // leaving the reader to hunt for it.
    tenureInsight() {
      const d = this.databarchart;
      if (!d || !d.labels || !d.labels.length || !d.worse || !d.worse.length) {
        return null;
      }
      const risks = d.worse;
      const happy = d.happy || [];
      const cohortSizes = d.cohort_size || d.people_count || [];
      const riskCounts = d.risk_count || [];
      const responseCounts = d.response_count || [];
      const validIndexes = risks
        .map((risk, index) => ({ risk, index }))
        .filter(({ risk, index }) => risk != null && (responseCounts[index] || 0) > 0);
      if (!validIndexes.length) return null;

      let maxIdx = validIndexes[0].index;
      validIndexes.forEach(({ risk, index }) => {
        if (risk > risks[maxIdx]) maxIdx = index;
      });
      const maxRisk = risks[maxIdx];
      const totalRisk = riskCounts.reduce((sum, value) => sum + (value || 0), 0);
      const totalResponses = responseCounts.reduce((sum, value) => sum + (value || 0), 0);
      if (!totalResponses) return null;
      const avg = (totalRisk * 100) / totalResponses;

      // Only call it out when it is materially high and above the response-
      // weighted company rate. This avoids treating tiny cohorts as equal to
      // cohorts with hundreds of check-ins.
      const show = maxRisk >= 10 && maxRisk >= avg * 1.4;
      const rawLabel = d.labels[maxIdx] || "";
      return {
        show,
        label: rawLabel.split("yr").join(" yr"),
        risk: maxRisk,
        happy: happy[maxIdx] != null ? happy[maxIdx] : "—",
        people: cohortSizes[maxIdx] != null ? cohortSizes[maxIdx] : 0,
        avg: Math.round(avg),
      };
    },
    // Net positive/neutral/negative balance of this week's most common tones,
    // computed from the tones already on screen. Answers "is our communication
    // net-positive this week?" before the per-tone detail.
    toneValence() {
      const tones = this.toneData || [];
      if (!tones.length) return null;
      let pos = 0,
        neu = 0,
        neg = 0,
        posAgo = 0,
        totalAgo = 0;
      tones.forEach((t) => {
        const v = this.toneValenceOf(t.toneId);
        const c = t.count || 0;
        const cAgo = t.countAgo || 0;
        if (v === "positive") {
          pos += c;
          posAgo += cAgo;
        } else if (v === "negative") {
          neg += c;
        } else {
          neu += c;
        }
        totalAgo += cAgo;
      });
      const total = pos + neu + neg;
      if (total === 0) return null;
      const pct = (n) => Math.round((n / total) * 100);
      const posPct = pct(pos);
      const neuPct = pct(neu);
      const negPct = 100 - posPct - neuPct;
      const posShareAgo = totalAgo > 0 ? (posAgo / totalAgo) * 100 : posPct;
      const posDelta = Math.round(posPct - posShareAgo);
      return {
        posPct,
        neuPct: neuPct < 0 ? 0 : neuPct,
        negPct: negPct < 0 ? 0 : negPct,
        posDelta,
        improving: posPct >= negPct,
      };
    },
    // Largest tone share on screen — bars are scaled against this so the top
    // tone fills the track and the rest read proportionally, instead of every
    // single-digit share collapsing into an unreadable sliver.
    toneMaxPct() {
      const tones = this.toneData || [];
      if (!tones.length) return 0;
      return Math.max(...tones.map((t) => t.percentage || 0));
    },
    maximumUsers() {
      return this.$store.getters["dashboard/maximumUsers"];
    },
    past12Hour() {
      return this.$store.getters["dashboard/past12Hour"];
    },
    activeRecord() {
      return this.$store.getters["dashboard/activeRecord"];
    },
    questionOpenEnd() {
      return this.$store.getters["dashboard/questionOpenEnd"];
    },
    activeOverview() {
      return this.$store.getters["dashboard/managerData"];
    },
    engagementpeople() {
      return this.$store.getters["dashboard/engagementpeople"];
    },
    enps() {
      return this.$store.getters["dashboard/enps"];
    },
    adoption() {
      return this.$store.getters["dashboard/adoption"];
    },
    monthlyActiveUser() {
      return this.$store.getters["dashboard/monthlyActiveUser"];
    },
    weeklyActiveUser() {
      return this.$store.getters["dashboard/weeklyActiveUser"];
    },
    miniTrends() {
      return this.$store.getters["dashboard/miniTrends"] || {};
    },
    engagementTeamBreakdown() {
      return this.$store.getters["dashboard/engagementTeamBreakdown"] || {};
    },
    engagementTeamBreakdownLoading() {
      return this.$store.getters["dashboard/engagementTeamBreakdownLoading"];
    },
    enpsLoading() {
      return this.$store.getters["dashboard/enpsLoading"];
    },
    impactRatingLoading() {
      return this.$store.getters["dashboard/impactRatingLoading"];
    },
    happinessLoading() {
      return this.$store.getters["dashboard/happinessLoading"];
    },
    feedbackLoading() {
      return this.$store.getters["dashboard/feedbackLoading"];
    },

    activityLoadingStatus() {
      return this.$store.getters["dashboard/activityLoadingStatus"];
    },
    benchmarkBreak() {
      return this.$store.getters["dashboard/benchmarkBreak"];
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    activeIsGreater() {
      return (
        parseInt(this.activeOverview.active) >
        (parseInt(this.activeOverview.passive) + parseInt(this.activeOverview.inactive))
      );
    },
    // Focus & Goal Alignment computed properties
    dashboardFocusSummary() {
      return this.$store.getters["dashboard/dashboardFocusSummary"];
    },
    dashboardFocusSummaryLoading() {
      return this.$store.getters["dashboard/dashboardFocusSummaryLoading"];
    },
    focusAlignmentLoading() {
      if (this.$store.getters["goals/goalsLoading"]) return true;
      if (!this.$store.getters["goals/goalsLoaded"] && !this.dashboardGoalsLoadFailed) return true;
      if (this.dashboardFocusSummary) return false;
      if (this.dashboardFocusSummaryLoading) return true;
      return this.$store.getters["focus/loadingHistorical"] || 
             this.$store.getters["focus/isLoadingHistoricalTeam"]('All Teams', true);
    },
    focusCoverageLoading() {
      if (this.dashboardFocusSummary) return false;
      if (this.dashboardFocusSummaryLoading) return true;
      return this.$store.getters["focus/loading"];
    },
    focusAlignmentData() {
      if (this.dashboardFocusSummary) {
        return this.dashboardFocusSummary;
      }
      return this.$store.getters["focus/getHistoricalSummaryStats"]('All Teams', true);
    },
    focusSummaryStats() {
      if (this.dashboardFocusSummary) {
        return this.dashboardFocusSummary;
      }
      return this.$store.getters["focus/summaryStats"];
    },
    organizationActiveGoalCount() {
      return this.$store.getters["goals/activeGoalsCount"] || 0;
    },
    goalAlignmentDisplay() {
      if (!this.$store.getters["goals/goalsLoaded"]) {
        return {
          status: 'not_available',
          alignmentRate: null,
          alignedItems: 0,
          unalignedItems: 0,
          unknownItems: 0,
        };
      }

      const data = this.focusAlignmentData || {};
      return resolveGoalAlignmentDisplay(
        data.alignedItems,
        data.unalignedItems,
        this.organizationActiveGoalCount
      );
    },
    goalAlignmentRate() {
      return this.goalAlignmentDisplay.alignmentRate;
    },
    isGoalAlignmentApplicable() {
      return this.goalAlignmentDisplay.status === 'assessed';
    },
    isGoalAlignmentUnknown() {
      return this.goalAlignmentDisplay.status === 'unknown';
    },
    goalAlignmentHealthColor() {
      const rate = this.goalAlignmentRate;
      if (rate === null) return '#909399'; // Gray for N/A
      if (rate < 33) return '#F44336'; // Red
      if (rate < 66) return '#FBC02D'; // Yellow
      return '#35D077'; // Green
    },
    focusCoverageRate() {
      return Math.round(this.focusCoverageRawRate);
    },
    focusCoverageRawRate() {
      if (this.dashboardFocusSummary) {
        return Number(this.dashboardFocusSummary.focusCoverage) || 0;
      }
      const stats = this.focusSummaryStats;
      if (!stats || stats.totalPeople === 0) return 0;
      return (stats.withFocus / stats.totalPeople) * 100;
    },
    focusCoverageHealthColor() {
      const rate = this.focusCoverageRate;
      if (rate < 33) return '#F44336'; // Red
      if (rate < 66) return '#FBC02D'; // Yellow
      return '#35D077'; // Green
    },
    focusCoverageLastWeekComparison() {
      const dashboardPrevious = this.dashboardFocusSummary &&
        this.dashboardFocusSummary.focusCoverageLastWeek;
      if (dashboardPrevious) {
        const previous = Number(dashboardPrevious.coverageRate) || 0;
        const change = Math.round((this.focusCoverageRawRate - previous) * 10) / 10;
        return {
          previous,
          change,
          trend: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
        };
      }

      const lastWeekSnapshot = this.focusCoverageLastWeekSnapshot;
      if (!lastWeekSnapshot) return null;

      const change = this.focusCoverageRawRate - lastWeekSnapshot.coverageRate;
      const roundedChange = Math.round(change * 10) / 10;
      const trend = roundedChange > 0 ? 'up' : roundedChange < 0 ? 'down' : 'neutral';

      return {
        previous: lastWeekSnapshot.coverageRate,
        change: roundedChange,
        trend,
      };
    },
    focusCoverageLastWeekSnapshot() {
      return this.$store.getters["focus/getFocusCoverageSnapshotDaysAgo"](7, 'All Teams');
    },
    // Weekly change computed properties
    goalAlignmentWeekChange() {
      const dashboardWeeklyComparison = this.dashboardFocusSummary &&
        this.dashboardFocusSummary.weeklyComparison;
      const dashboardCurrent = dashboardWeeklyComparison && dashboardWeeklyComparison.current;
      const dashboardPrevious = dashboardWeeklyComparison && dashboardWeeklyComparison.previous;
      if (dashboardCurrent && dashboardPrevious && dashboardPrevious.alignmentRate !== null) {
        const current = dashboardCurrent.alignmentRate;
        if (current === null) {
          return { current: 0, previous: null, change: null, trend: 'no_data' };
        }

        const previous = Number(dashboardPrevious.alignmentRate) || 0;
        const change = Math.round((Number(current) - previous) * 10) / 10;
        return {
          current: Number(current),
          previous,
          change,
          trend: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
        };
      }

      return this.$store.getters["focus/getGoalAlignmentWeekChange"]('All Teams');
    },
    loadingWeeklySummary() {
      if (this.dashboardFocusSummary) return false;
      if (this.dashboardFocusSummaryLoading) return true;
      return this.$store.getters["focus/isLoadingWeeklySummary"]('All Teams');
    },
    // Multi-week trend data for retrieving latest weekly values
    focusCoverageTrendData() {
      return this.$store.getters["focus/getWeeklyTrendData"]('All Teams', 'focusCoverage', 2);
    },
    goalAlignmentTrendData() {
      return this.$store.getters["focus/getWeeklyTrendData"]('All Teams', 'alignedItemsPercentage', 2);
    },
    // Learning Journeys computed properties
    learningOverview() {
      return this.$store.getters["learningJourneys/overview"] || { active: 0, completed: 0, upskilling: 0, learning_time: 0 };
    },
    learningLoading() {
      return this.$store.getters["learningJourneys/loading"];
    },
  },
  beforeDestroy() {
    this.pauseDashboardLiveUpdates();
    this.$store.dispatch("dashboard/closePast12Hours", this.user);
    this.$store.dispatch("dashboard/closeAdoption", this.user);
    // Clean up event listener
    this.$root.$off('languageChanged', this.handleLanguageChange);
    document.removeEventListener('visibilitychange', this.handleDashboardVisibilityChange);
    this.destroySentimentChart();
    if (this.dashboardFocusIdleCallback && window.cancelIdleCallback) {
      window.cancelIdleCallback(this.dashboardFocusIdleCallback);
      this.dashboardFocusIdleCallback = null;
    }
  },
  data() {
    return {
      dialogVisible: false,
      dialogDimensionVisible: false,
      dimensionData: [
        {
          dimpic: "",
          name: "",
          quest_id: "",
        },
      ],
      talkingg_point_bar_chart: "",
      engagement_score_bar_chart: "",
      dashboard_bar_chart: "",
      real_time_chart: "",
      maxLength: 60,
      questionsTableKey: 0, // Key to force table re-render when language changes
      //benchmarkBreak: "eNPS",
      chartOptions: {
        // responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "none",
        },
        title: {
          display: false,
        },
        hover: {
          animationDuration: 0,
        },
        scales: {
          yAxes: [
            {
              stacked: false,
              ticks: {
                display: false,
                beginAtZero: true,
                stepSize: 20,
              },
              gridLines: {
                display: false,
                drawBorder: false,
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                display: false,
                fontColor: "black",
              },
            },
          ],
        },
        layout: {
          padding: {
            top: 18,
          },
        },
      },
      talkingPointsTimeLineCallAPI: 0,
      engagementImpactLoadingStatus: true,
      weeklyLoadingStatus: true,
      miniTrendWidth: 40,
      miniTrendHeight: 16,
      commHealthDialog: false,
      commHealthType: null,
      talkingPointDialog: false,
      tpActiveItem: null,
      teamHight: '',
      sentimentChartInstance: null,
      sentimentChartUnwatch: null,
      activeSentiments: { positive: true, neutral: true, negative: true },
      viewportDragging: false,
      viewportDragStartX: 0,
      viewportScrollStartLeft: 0,
      currentViewportIndex: -1,
      boundHandleViewportDrag: null,
      boundStopViewportDrag: null,
      boundOnContentScroll: null,
      visibleMonthKeys: {},
      animatedMonthKeys: {},
      monthObserver: null,
      monthContentHeights: {},
      _scrollRafId: null,
      dashboardUsers: null,
      dashboardActivityRefreshTimer: null,
      dashboardAdoptionRefreshTimer: null,
      dashboardActivityRefreshInFlight: false,
      dashboardAdoptionRefreshInFlight: false,
      dashboardInitializing: false,
      dashboardLoadRunId: 0,
      dashboardFocusIdleCallback: null,
      dashboardGoalsLoadFailed: false,
    };
  },
  watch: {
    // Watch for user to become available
    user: {
      async handler(newUser, oldUser) {
        // Initialize dashboard when user first becomes available
        if (!newUser) {
          this.pauseDashboardLiveUpdates();
          this.dashboardUsers = null;
          return;
        }

        if (
          !oldUser ||
          newUser.company !== oldUser.company ||
          newUser.email !== oldUser.email
        ) {
          await this.initializeDashboard();
        }
      },
      immediate: false
    },
    // Watch miniTrends to trigger re-render when individual trends load
    miniTrends: {
      handler() {
        // This will trigger Vue to re-evaluate the template when miniTrends updates
      },
      deep: true
    },
    datachart() {
      if (this.talkingg_point_bar_chart)
        this.talkingg_point_bar_chart.destroy();
      this.randerChart();
    },
    databarchart() {
      if (this.dashboard_bar_chart) this.dashboard_bar_chart.destroy();
      this.randerBarChart();
    },
    databarchartEngagementScore() {
      if (this.engagement_score_bar_chart)
        this.engagement_score_bar_chart.destroy();
      this.renderChartEngagementScoreCompany();
      this.renderChartEngagementScoreManager();
      this.renderChartEngagementScoreLeader();
    },

    activeRecord() {
      if (this.real_time_chart) this.real_time_chart.destroy();
      this.randerLineChart();
    },
    enpsLoading() {
      this.setEngagementImpactLoading();
    },

    impactRatingLoading() {
      this.setEngagementImpactLoading();
    },
    happinessLoading() {
      this.setWeeklyLoadingStatus();
    },
    feedbackLoading() {
      this.setWeeklyLoadingStatus();
    },
    tabActive(val) {
      if (this.talkingg_point_bar_chart) {
        this.talkingg_point_bar_chart.destroy();
      }
      if (this.dashboard_bar_chart) {
        this.dashboard_bar_chart.destroy();
      }
      if (this.real_time_chart) {
        this.real_time_chart.destroy();
      }

      if (val == "dashboard") {
        this.$nextTick(() => {
          if (this.tabActive !== "dashboard") {
            return;
          }

          this.resumeDashboardLiveUpdates({ immediate: false });
          this.randerChart();
          this.randerBarChart();
          this.randerLineChart();
        });
      } else {
        this.pauseDashboardLiveUpdates();
      }
    },
    enps: {
      handler(newVal) {
        // Watch for eNPS changes
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    isDashboardRefreshActive() {
      const dashboardTabActive = !this.tabActive || this.tabActive === "dashboard";
      return !!this.user && dashboardTabActive && !document.hidden;
    },
    clearDashboardRefreshTimers() {
      if (this.dashboardActivityRefreshTimer) {
        clearTimeout(this.dashboardActivityRefreshTimer);
        this.dashboardActivityRefreshTimer = null;
      }

      if (this.dashboardAdoptionRefreshTimer) {
        clearTimeout(this.dashboardAdoptionRefreshTimer);
        this.dashboardAdoptionRefreshTimer = null;
      }
    },
    pauseDashboardLiveUpdates() {
      this.clearDashboardRefreshTimers();
    },
    async resumeDashboardLiveUpdates({ immediate = false } = {}) {
      this.clearDashboardRefreshTimers();

      if (!this.isDashboardRefreshActive()) {
        return;
      }

      if (immediate) {
        await Promise.all([
          this.refreshDashboardPast12Hours(),
          this.refreshDashboardAdoption(),
        ]);
      }

      this.scheduleDashboardActivityRefresh();
      this.scheduleDashboardAdoptionRefresh();
    },
    scheduleDashboardActivityRefresh(delay = DASHBOARD_ACTIVITY_REFRESH_MS) {
      if (!this.isDashboardRefreshActive()) {
        return;
      }

      this.dashboardActivityRefreshTimer = window.setTimeout(async () => {
        await this.refreshDashboardPast12Hours();
        this.scheduleDashboardActivityRefresh();
      }, delay);
    },
    scheduleDashboardAdoptionRefresh(delay = DASHBOARD_ADOPTION_REFRESH_MS) {
      if (!this.isDashboardRefreshActive()) {
        return;
      }

      this.dashboardAdoptionRefreshTimer = window.setTimeout(async () => {
        await this.refreshDashboardAdoption();
        this.scheduleDashboardAdoptionRefresh();
      }, delay);
    },
    async refreshDashboardPast12Hours() {
      if (!this.user || this.dashboardActivityRefreshInFlight) {
        return;
      }

      this.dashboardActivityRefreshInFlight = true;
      try {
        await this.$store.dispatch("dashboard/getPast12Hours", this.user);
      } finally {
        this.dashboardActivityRefreshInFlight = false;
      }
    },
    async refreshDashboardAdoption() {
      if (!this.user || this.dashboardAdoptionRefreshInFlight) {
        return;
      }

      this.dashboardAdoptionRefreshInFlight = true;
      try {
        if (!this.dashboardUsers || Object.keys(this.dashboardUsers).length === 0) {
          const result = await this.$store.dispatch("dashboard/getMemberAndTeam", this.user);
          this.dashboardUsers = result.users;
        }
        await this.$store.dispatch("dashboard/getAdoption", this.dashboardUsers);
      } finally {
        this.dashboardAdoptionRefreshInFlight = false;
      }
    },
    async handleDashboardVisibilityChange() {
      if (this.isDashboardRefreshActive()) {
        await this.resumeDashboardLiveUpdates({ immediate: true });
        return;
      }

      this.pauseDashboardLiveUpdates();
    },
    loadDashboardFocusInBackground(runId) {
      const loadFocus = () => {
        if (runId !== this.dashboardLoadRunId || !this.user) {
          return;
        }

        this.dashboardGoalsLoadFailed = false;
        this.$store.dispatch("goals/getGoalsTeamAI").catch((error) => {
          this.dashboardGoalsLoadFailed = true;
          console.error("[dashboard] goals load failed", error);
        });

        this.$store.dispatch("dashboard/getDashboardFocusSummary", this.user).catch((error) => {
          console.warn("[dashboard] focus summary API failed, using existing focus loaders", error);
          return Promise.all([
            this.$store.dispatch("focus/loadHistoricalFocusDataForTeam", {
              teamId: 'All Teams',
              includeChildren: true,
            }),
            this.$store.dispatch("focus/loadFocusData"),
            this.$store.dispatch("focus/loadWeeklySummaryForTeam", {
              teamId: 'All Teams',
            }),
          ]);
        }).catch((error) => {
          console.error("[dashboard] background focus load failed", error);
        });
      };

      if (window.requestIdleCallback) {
        this.dashboardFocusIdleCallback = window.requestIdleCallback(loadFocus, {
          timeout: 2000,
        });
        return;
      }

      window.setTimeout(loadFocus, 0);
    },
    async initializeDashboard() {
      if (!this.user || this.dashboardInitializing) return;

      this.dashboardInitializing = true;
      const dashboardLoadRunId = this.dashboardLoadRunId + 1;
      this.dashboardLoadRunId = dashboardLoadRunId;

      const trackDashboardLoad = (name, loadPromise) => {
        Promise.resolve(loadPromise).catch((error) => {
          console.error(`[dashboard] ${name} load failed`, error);
        });

        return loadPromise;
      };

      this.loadDashboardFocusInBackground(dashboardLoadRunId);

      try {
        const result = await trackDashboardLoad(
          "memberAndTeam",
          this.$store.dispatch("dashboard/getMemberAndTeam", this.user)
        );

        this.dashboardUsers = result.users;
        await trackDashboardLoad("weekOfYear", this.$store.dispatch("dashboard/setWeekOfYear"));

        trackDashboardLoad("manager", this.$store.dispatch("dashboard/getManager", result));
        trackDashboardLoad("adoption", this.$store.dispatch("dashboard/getAdoption", result.users));
        trackDashboardLoad("impactRating", this.$store.dispatch("dashboard/getImpactRating", result.users));
        trackDashboardLoad("enps", this.$store.dispatch("dashboard/getEnps", {
          user: this.user,
          teams: result.teams,
        }));
        this.teamHight = result.teamHight;
        trackDashboardLoad("past12Hours", this.$store.dispatch("dashboard/getPast12Hours", this.user));
        trackDashboardLoad("globalVariable", this.$store.dispatch("dashboard/getGlobalVariable", this.$global_variable));
        trackDashboardLoad("activityThisWeek", this.$store.dispatch("dashboard/getActivityThisWeek", this.user));
        trackDashboardLoad("happinessThisWeek", this.$store.dispatch("dashboard/getHappinessThisWeek", this.user));
        trackDashboardLoad("feedbackThisWeek", this.$store.dispatch("dashboard/getFeedbackThisWeek", this.user));
        trackDashboardLoad("questionData", this.$store.dispatch("dashboard/getQuestionData", this.user));
        trackDashboardLoad("questionsName", this.$store.dispatch("dashboard/getQuestionsName"));

        trackDashboardLoad("talkPointsDaily", this.$store.dispatch("dashboard/getTalkPointsDaily", this.user));
        trackDashboardLoad("dataJobTenure", this.$store.dispatch("dashboard/getDataJobTenure", this.user));
        trackDashboardLoad("dataKeyGroup", this.$store.dispatch("dashboard/getDataKeyGroup", this.user));
        trackDashboardLoad("dataToneAnalyze", this.$store.dispatch("dashboard/getDataToneAnalyze", this.user));
        trackDashboardLoad("questionOpenEnd", this.$store.dispatch("dashboard/getQuestionOpenEnd", this.user));
        trackDashboardLoad("dataEngagementScore", this.$store.dispatch("dashboard/getDataEngagementScore", this.user));
        trackDashboardLoad("comEncryp", this.$store.dispatch("dashboard/getComEncryp", this.user));
        trackDashboardLoad("miniTrends", this.$store.dispatch("dashboard/refreshMiniTrends"));

        // Load learning journeys data
        trackDashboardLoad("learningJourneys", this.$store.dispatch("learningJourneys/getLearningJourneys"));

        this.resumeDashboardLiveUpdates({ immediate: false });
      } catch (error) {
        console.error("[dashboard] initial load failed", error);
      } finally {
        this.dashboardInitializing = false;
      }
    },
    // Single source of truth for tone valence. tone ids arrive lowercased and
    // hyphenated (the store does key.replace("_","-").toLowerCase()).
    toneValenceOf(toneId) {
      const positive = [
        'joy', 'joyful', 'satisfied', 'excited', 'polite', 'sympathetic',
        'appreciative', 'encouraging', 'optimistic', 'friendly', 'respectful',
        'empathetic', 'patient', 'collaborative', 'open-minded', 'constructive',
        'confident'
      ];
      const negative = [
        'anger', 'fear', 'sadness', 'sad', 'frustrated', 'impolite', 'aggressive',
        'passive-aggressive', 'dismissive', 'gossiping', 'overly-critical',
        'gibberish', 'toxic', 'disrespectful', 'condescending', 'accusatory',
        'disapproving', 'disheartening', 'impatient', 'sarcastic', 'defensive',
        'rude', 'egocentric'
      ];
      if (positive.includes(toneId)) return 'positive';
      if (negative.includes(toneId)) return 'negative';
      return 'neutral';
    },
    isNegativeTone(toneId) {
      return this.toneValenceOf(toneId) === 'negative';
    },
    toneBarColor(toneId) {
      const v = this.toneValenceOf(toneId);
      if (v === 'positive') return '#27bc6c';
      if (v === 'negative') return '#f44336';
      return '#9aa0ad';
    },
    // Scale a tone's share against the top tone so the bar lengths compare.
    // The top tone fills ~96% (a sliver of track kept so it reads as relative,
    // not an absolute 100%); the printed % beside it carries the true value.
    toneBarPct(pct) {
      const max = this.toneMaxPct;
      if (!max) return 0;
      return Math.min(100, Math.round(((pct || 0) / max) * 96));
    },
    // Change pill class with honest good/bad semantics: a rising positive tone
    // or a falling negative tone is "good"; the inverse is "bad"; flat/neutral
    // reads muted. Fixes the old logic where "-2%" could show green with no cue.
    toneChangeClass(data) {
      const change = data.percentageChange || 0;
      if (change === 0) return 'neutral';
      const v = this.toneValenceOf(data.toneId);
      if (v === 'neutral') return 'neutral';
      const good = v === 'positive' ? change > 0 : change < 0;
      return good ? 'good' : 'bad';
    },
    // --- Engagement-by-dimension table helpers ---
    teamTotal(row) {
      const s = (row && row.status) || {};
      return (s.green || 0) + (s.yellow || 0) + (s.red || 0);
    },
    belowHealthy(row) {
      const s = (row && row.status) || {};
      return (s.yellow || 0) + (s.red || 0);
    },
    ragWidth(row, band) {
      const total = this.teamTotal(row);
      if (!total) return 0;
      return ((row.status[band] || 0) / total) * 100;
    },
    // Maps a 0-100 success score onto the shared score-band-* design tokens.
    scoreBand(success) {
      const s = parseFloat(success) || 0;
      if (s <= 0) return 'band-zero';
      if (s >= 75) return 'band-high';
      if (s >= 60) return 'band-moderate';
      return 'band-low';
    },
    // Lazy-load the per-team breakdown the first time any dimension row is expanded.
    // The store action self-guards against re-fetching, so firing on every toggle is safe.
    onDimensionExpand() {
      this.$store.dispatch("dashboard/getEngagementTeamBreakdown");
    },
    teamBreakdownFor(questId) {
      return this.engagementTeamBreakdown[questId] || [];
    },
    // Critical (red) teams only — the urgent triage list. The store already sorts the
    // breakdown worst-first, so the red subset stays lowest-score-first.
    criticalTeamsFor(questId) {
      return this.teamBreakdownFor(questId).filter((t) => t.band === 'red');
    },
    // green/yellow/red band -> solid score-band-* token (the colored dot).
    bandDotClass(band) {
      if (band === 'green') return 'score-band-high';
      if (band === 'yellow') return 'score-band-moderate';
      if (band === 'red') return 'score-band-low';
      return 'score-band-zero';
    },
    // green/yellow/red band -> soft qd-score-chip band-* token (the score chip).
    bandChipClass(band) {
      if (band === 'green') return 'band-high';
      if (band === 'yellow') return 'band-moderate';
      if (band === 'red') return 'band-low';
      return 'band-zero';
    },
    async handleLanguageChange(langCode) {
      // Refresh question names when language changes
      await this.$store.dispatch("dashboard/getQuestionsName");
      // Force table re-render by changing the key
      this.questionsTableKey += 1;
    },
    randerLineChart() {
      var chart = this.$refs.pastLinechart;
      if (chart) {
        const self = this;
        const lineChartData = {
          labels: this.activeRecord.time,
          datasets: [
            {
              backgroundColor: "rgba(53, 208, 119, 0.16)",
              borderColor: "#35D077",
              fill: true,
              pointHoverBackgroundColor: "#35D077",
              pointBackgroundColor: "#35D077",
              pointRadius: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 4],
              lineTension: 0,
              data: this.activeRecord.data,
              pointHitRadius: 6,
              pointHoverRadius: 5,
              borderWidth: 2,
            },
          ],
        };
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));
        options.plugins = {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "index",
            intersect: false,
            position: "nearest",
            displayColors: false,
            callbacks: {
              title: function (items) {
                return (items && items[0] && items[0].label) || "";
              },
              label: function (tooltipItems) {
                const value = tooltipItems.formattedValue;
                return `${value} ${self.$t("insights.Interactions_caption")}`;
              },
            },
          },
        };
        options.scales.y.display = false;
        options.scales.x.display = false;
        this.real_time_chart = new Chart(ctx, {
          type: "line",
          data: lineChartData,
          options: options,
        });
      }
    },
    randerBarChart() {
      var chart = this.$refs.barchart;
      // Guard clause: ensure databarchart and its labels exist before proceeding
      if (!this.databarchart || !this.databarchart.labels) {
        return;
      }
      // Tenure is an ordered journey (newest hires -> longest tenured), so we
      // read it left-to-right as lines rather than 20 grouped bars. Happiness
      // and Risk carry the story; Active is a faint reference line. Headcount
      // per cohort rides on the x-axis so a high rate on a tiny cohort can't
      // masquerade as a real signal.
      let chartlabels = [];
      this.databarchart.labels.forEach(function (item) {
        chartlabels.push(item.split("yr").join(" yr"));
      });
      const cohortSizes = this.databarchart.cohort_size || this.databarchart.people_count || [];
      if (chart) {
        const lineChartData = {
          labels: chartlabels,
          datasets: [
            {
              label: "Happiness",
              borderColor: "#27bc6c",
              backgroundColor: "#27bc6c",
              pointBackgroundColor: "#27bc6c",
              pointRadius: 3,
              borderWidth: 2,
              tension: 0.35,
              data: this.databarchart.happy,
              metric_count: this.databarchart.happy_count || [],
              denominator: this.databarchart.response_count || [],
              metric_kind: "happiness",
              empty_label: "check-ins",
              datalabels: { display: false },
            },
            {
              label: "Risk",
              borderColor: "#f44336",
              backgroundColor: "#f44336",
              pointBackgroundColor: "#f44336",
              pointRadius: 3,
              borderWidth: 2,
              tension: 0.35,
              data: this.databarchart.worse,
              metric_count: this.databarchart.risk_count || [],
              denominator: this.databarchart.response_count || [],
              metric_kind: "risk",
              empty_label: "check-ins",
              datalabels: { display: false },
            },
            {
              label: "Active",
              borderColor: "#7E3FC7",
              backgroundColor: "#7E3FC7",
              pointBackgroundColor: "#7E3FC7",
              pointRadius: 2,
              borderWidth: 1.5,
              borderDash: [4, 4],
              tension: 0.35,
              data: this.databarchart.active,
              metric_count: this.databarchart.active_count || [],
              denominator: cohortSizes,
              metric_kind: "active",
              empty_label: "people in this cohort",
              datalabels: { display: false },
            },
            {
              label: "Open Feedback",
              borderColor: "#64748b",
              backgroundColor: "#64748b",
              pointBackgroundColor: "#64748b",
              pointStyle: "rectRot",
              pointRadius: 2.5,
              borderWidth: 1.5,
              borderDash: [2, 3],
              tension: 0.35,
              data: this.databarchart.feedback,
              metric_count: this.databarchart.feedback_count || [],
              denominator: this.databarchart.feedback_prompt_count || [],
              metric_kind: "feedback",
              empty_label: "open-ended prompts",
              datalabels: { display: false },
            },
          ],
        };
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));
        options.plugins.datalabels = { display: false };
        options.plugins.legend = {
          display: true,
          position: "top",
          align: "start",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            boxHeight: 8,
            padding: 14,
            font: { family: "Lato", size: 11 },
            color: "#6a6a6a",
          },
        };
        options.scales.y.suggestedMin = 0;
        options.scales.y.suggestedMax = 100;
        options.scales.x.ticks.color = "#3f3f46";
        options.scales.x.ticks.font = { family: "Lato", size: 11, weight: "600" };
        options.scales.x.ticks.callback = function (value, index) {
          return chartlabels[index] || "";
        };
        // Reserve vertical room beneath the axis for the headcount sub-label.
        options.layout = { padding: { bottom: 18 } };
        options.plugins.tooltip = {
          mode: "index",
          position: "nearest",
          padding: 10,
          bodySpacing: 5,
          boxPadding: 4,
          callbacks: {
            label: function (tooltipItems) {
              const label = tooltipItems.dataset.label;
              const value = tooltipItems.formattedValue;
              const count = tooltipItems.dataset.metric_count[tooltipItems.dataIndex];
              const denominator = tooltipItems.dataset.denominator[tooltipItems.dataIndex];
              const kind = tooltipItems.dataset.metric_kind;
              if (tooltipItems.raw == null) {
                return `${label}: No ${tooltipItems.dataset.empty_label}`;
              }
              const detailByKind = {
                happiness: `${count}/${denominator} check-ins`,
                risk: `${count}/${denominator} check-ins`,
                active: `${count}/${denominator} people`,
                feedback: `${count}/${denominator} prompts answered`,
              };
              return `${label}: ${value}% · ${detailByKind[kind]}`;
            },
          },
        };
        // The tenure bucket is the primary axis label; the cohort headcount
        // rides just beneath it in a quieter style so the two read as a
        // hierarchy instead of one indistinguishable two-line block.
        const headcountAxisPlugin = {
          id: "tenureHeadcount",
          afterDraw(chartInstance) {
            const xAxis = chartInstance.scales.x;
            if (!xAxis) return;
            const c = chartInstance.ctx;
            c.save();
            c.font = "10px Lato";
            c.fillStyle = "#9a9a9a";
            c.textAlign = "center";
            c.textBaseline = "top";
            xAxis.ticks.forEach(function (tick, index) {
              const ppl = cohortSizes[index];
              if (ppl == null) return;
              c.fillText(
                ppl + " people",
                xAxis.getPixelForTick(index),
                xAxis.bottom + 3
              );
            });
            c.restore();
          },
        };
        const ctx = chart.getContext("2d");
        this.dashboard_bar_chart = new Chart(ctx, {
          type: "line",
          plugins: [ChartDataLabels, headcountAxisPlugin],
          data: lineChartData,
          options: options,
        });
      }
    },
    randerChart() {
      var chart = this.$refs.chartTalkingPoints;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));
        options.responsive = true;
        options.maintainAspectRatio = false;
        // const data = {
        //   datasets: [
        //     {
        //       label: "My First Dataset",
        //       data: [65, 59, 80, 81, 56, 55, 40],
        //       fill: false,
        //       borderColor: "rgb(75, 192, 192)",
        //       tension: 0.1,
        //     },
        //   ],
        // };
        this.talkingg_point_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: this.opt_month_name,
            datasets: [
              {
                label: "SUCCESS SCORE",
                data: this.datachart.success,
                newLabels: this.newLabels,
                backgroundColor: "rgba(226, 215, 244, 1)",
                fill: true,
                borderColor: "rgba(108, 48, 204, 1)",
                borderWidth: 1,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: {
                max: 100,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value + "%";
                  },
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                },
              },
            },
            plugins: {
              datalabels: {
                formatter: function (value, context) {
                  return value + "%";
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: function (tooltipItems) {
                    return tooltipItems[0].dataset.newLabels[
                      tooltipItems[0].dataIndex
                    ];
                  },
                  label: function (tooltipItems) {
                    const label = tooltipItems.dataset.label;
                    const value = tooltipItems.formattedValue;
                    return `${label}: ${value}%`;
                  },
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    renderChartEngagementScoreCompany() {
      var chart = this.$refs.chartEngagementScoreCompany;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));

        options.responsive = true;
        options.maintainAspectRatio = false;
        let opt_month_name = this.databarchartEngagementScore['labels']
        let maxScore = this.databarchartEngagementScore['maxScore']
        let yMax = maxScore < 50 ? 50 : (maxScore < 75 ? 75 : 100);
        this.engagement_score_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: opt_month_name,
            datasets: [
              {
                // label: "Jun 12",
                data: this.databarchartEngagementScore['company'],
                newLabels: this.databarchartEngagementScore['newLabels'],
                backgroundColor: "#32C2D5",
                fill: true,
                borderColor: "#32C2D5",
                borderWidth: 1,
                pointRadius: 0
              },
            ],
          },
          options: {
            interaction: {
              intersect: false,
              mode: 'nearest'
            },
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false,
                max: yMax,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value;
                  },
                },
                grid: {
                  display: false,
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    color: 'black',
                    size: 12,
                    weight: 'bold',
                    family: 'Lato'
                  },
                },
                grid: {
                  display: false,
                },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              datalabels: {
                formatter: function (value, context) {
                  return value;
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: (tooltipItems) => {
                    return this.databarchartEngagementScore['newLabels'][tooltipItems[0].dataIndex];
                  },
                  label: function (tooltipItems) {
                    const value = tooltipItems.formattedValue;
                    return ` ${value}`;
                  },
                },
                titleFont: {
                  weight: 'bold',
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    renderChartEngagementScoreManager() {
      var chart = this.$refs.chartEngagementScoreManager;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));

        options.responsive = true;
        options.maintainAspectRatio = false;
        let opt_month_name = this.databarchartEngagementScore['labels']
        let maxScore = this.databarchartEngagementScore['maxScore']
        let yMax = maxScore < 50 ? 50 : (maxScore < 75 ? 75 : 100);
        this.engagement_score_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: opt_month_name,
            datasets: [
              {
                // label: "Jun 12",
                data: this.databarchartEngagementScore['manager'],
                newLabels: this.databarchartEngagementScore['newLabels'],
                backgroundColor: "#E45006",
                fill: true,
                borderColor: "#E45006",
                borderWidth: 1,
                pointRadius: 0
              },
            ],
          },
          options: {
            interaction: {
              intersect: false,
              mode: 'nearest'
            },
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false,
                max: yMax,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value;
                  },
                },
                grid: {
                  display: false,
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    color: 'black',
                    size: 12,
                    weight: 'bold',
                    family: 'Lato'
                  },
                },
                grid: {
                  display: false,
                },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              datalabels: {
                formatter: function (value, context) {
                  return value;
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: (tooltipItems) => {
                    return this.databarchartEngagementScore['newLabels'][tooltipItems[0].dataIndex];
                  },
                  label: function (tooltipItems) {
                    const value = tooltipItems.formattedValue;
                    return ` ${value}`;
                  },
                },
                titleFont: {
                  weight: 'bold',
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    renderChartEngagementScoreLeader() {
      var chart = this.$refs.chartEngagementScoreLeader;
      if (chart) {
        var ctx = chart.getContext("2d");
        const options = JSON.parse(JSON.stringify(this.variable.chartOptioins));

        options.responsive = true;
        options.maintainAspectRatio = false;
        let opt_month_name = this.databarchartEngagementScore['labels']
        let maxScore = this.databarchartEngagementScore['maxScore']
        let yMax = maxScore < 50 ? 50 : (maxScore < 75 ? 75 : 100);
        this.engagement_score_bar_chart = new Chart(ctx, {
          type: "line",
          data: {
            labels: opt_month_name,
            datasets: [
              {
                // label: "Jun 12",
                data: this.databarchartEngagementScore['leader'],
                newLabels: this.databarchartEngagementScore['newLabels'],
                backgroundColor: "#E048DA",
                fill: true,
                borderColor: "#E048DA",
                borderWidth: 1,
                pointRadius: 0
              },
            ],
          },
          options: {
            interaction: {
              intersect: false,
              mode: 'nearest'
            },
            maintainAspectRatio: false,
            scales: {
              y: {
                display: false,
                max: yMax,
                min: 0,
                ticks: {
                  stepSize: 50,
                  callback: function (value) {
                    return value;
                  },
                },
                grid: {
                  display: false,
                },
              },
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  font: {
                    color: 'black',
                    size: 12,
                    weight: 'bold',
                    family: 'Lato'
                  },
                },
                grid: {
                  display: false,
                },
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              datalabels: {
                formatter: function (value, context) {
                  return value;
                },
                font: {
                  weight: "bold",
                  size: 10,
                },
              },
              tooltip: {
                mode: "index",
                position: "nearest",
                callbacks: {
                  title: (tooltipItems) => {
                    return this.databarchartEngagementScore['newLabels'][tooltipItems[0].dataIndex];
                  },
                  label: function (tooltipItems) {
                    const value = tooltipItems.formattedValue;
                    return ` ${value}`;
                  },
                },
                titleFont: {
                  weight: 'bold',
                },
              },
            },
            elements: {
              line: {
                tension: 0,
              },
            },
            responsive: true,
          },
        });
      }
    },
    sortBy(type) {
      const labels = {
        mostred: "insights.Most_teams_red",
        biggestdrop: "insights.Biggest_drop",
        lowest: "insights.Lowest_Success_Score",
        highest: "insights.Highest_Success_Score",
      };
      if (labels[type]) {
        document.getElementById("sortby").innerHTML = this.$t(labels[type]);
      }
      this.$store.dispatch("dashboard/sortBy", type);
    },
    getImgUrlDim(pic) {
      var images = require.context(
        "../../assets/images/dimension/",
        false,
        /\.png$/
      );
      return images("./" + pic + ".png");
    },
    getImgUrl(pic) {
      var images = require.context("../../assets/images/", false, /\.png$/);
      return images("./" + pic + ".png");
    },
    // Opens the Communication Health detail dialog for a metric (only when
    // there is something to show).
    openCommHealth(type) {
      const count = type === 'toxic'
        ? (this.toxicTone.count || 0)
        : (this.gibbrishTone.count || 0);
      if (!count) return;
      this.commHealthType = type;
      this.commHealthDialog = true;
    },
    // Proportion width for a team's breakdown bar, floored so the smallest
    // count still renders a visible sliver.
    barW(count, max) {
      return Math.max(8, Math.round((count / (max || 1)) * 100)) + '%';
    },
    // A talking point is drillable when it carries supporting examples.
    // Leadership keeps the existing gate (no raw example access).
    tpHasDetails(item) {
      return this.user && this.user.user_type !== 'leadership'
        && item && item.point && Array.isArray(item.point.specific_examples)
        && item.point.specific_examples.length > 0;
    },
    openTalkingPoint(item) {
      if (!this.tpHasDetails(item)) return;
      this.tpActiveItem = item;
      this.talkingPointDialog = true;
    },
    showList() {
      this.$store.dispatch("dashboard/getDataTalkingPointsMonthly", this.user);
      this.activeSentiments = { positive: true, neutral: true, negative: true };
      this.currentViewportIndex = -1;
      this.monthContentHeights = {};
      this.animatedMonthKeys = {};
      // Initialize all months as visible to avoid placeholder flash on first render;
      // the IntersectionObserver will then manage visibility as the user scrolls
      const monthly = this.talking_points_monthly;
      const initVisible = {};
      if (monthly) {
        Object.keys(monthly).forEach(key => { initVisible[key] = true; });
      }
      this.visibleMonthKeys = initVisible;
      this.dialogVisible = true;
      this.$nextTick(() => {
        if (this.sentimentChartUnwatch) this.sentimentChartUnwatch();
        this.sentimentChartUnwatch = this.$watch('sentimentChartData', (val) => {
          if (val) {
            this.renderSentimentChart();
            this.$nextTick(() => {
              this.initViewportSync();
              this.setupMonthObserver();
            });
          }
        }, { immediate: true });
      });
    },
    renderSentimentChart() {
      const data = this.sentimentChartData;
      if (!data) return;
      const canvas = this.$refs.sentimentChart;
      if (!canvas) return;
      if (this.sentimentChartInstance) {
        this.sentimentChartInstance.destroy();
        this.sentimentChartInstance = null;
      }
      const ctx = canvas.getContext('2d');
      const neutralHalf = data.neutral.map(v => Math.ceil(v / 2));
      const neutralHalfLower = data.neutral.map(v => Math.floor(v / 2));
      const totalMonths = data.labels.length;
      this.sentimentChartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.labels,
          datasets: [
            {
              label: 'Positive',
              data: data.positive.map((v, i) => v + neutralHalf[i]),
              backgroundColor: this.activeSentiments.positive ? 'rgba(36, 157, 42, 0.45)' : 'rgba(200, 200, 200, 0.15)',
              borderColor: this.activeSentiments.positive ? '#249d2a' : '#ccc',
              borderWidth: 1.5,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: '#249d2a',
              order: 2,
            },
            {
              label: 'Neutral (upper)',
              data: neutralHalf,
              backgroundColor: this.activeSentiments.neutral ? 'rgba(180, 180, 180, 0.3)' : 'rgba(200, 200, 200, 0.1)',
              borderColor: this.activeSentiments.neutral ? '#bbb' : '#ddd',
              borderWidth: 1,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHoverBackgroundColor: '#999',
              order: 3,
            },
            {
              label: 'Negative',
              data: data.negative.map((v, i) => -(v + neutralHalfLower[i])),
              backgroundColor: this.activeSentiments.negative ? 'rgba(244, 67, 54, 0.4)' : 'rgba(200, 200, 200, 0.15)',
              borderColor: this.activeSentiments.negative ? '#f44336' : '#ccc',
              borderWidth: 1.5,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: '#f44336',
              order: 2,
            },
            {
              label: 'Neutral (lower)',
              data: neutralHalfLower.map(v => -v),
              backgroundColor: this.activeSentiments.neutral ? 'rgba(180, 180, 180, 0.3)' : 'rgba(200, 200, 200, 0.1)',
              borderColor: this.activeSentiments.neutral ? '#bbb' : '#ddd',
              borderWidth: 1,
              fill: 'origin',
              tension: 0.45,
              pointRadius: 0,
              pointHoverRadius: 4,
              pointHoverBackgroundColor: '#999',
              order: 3,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          onClick: (event, elements) => {
            if (this.viewportDragging) return;
            if (elements && elements.length > 0) {
              const idx = elements[0].index;
              const monthKey = data.keys[idx];
              this.scrollToMonth(monthKey);
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: (context) => {
                  const idx = context.dataIndex;
                  const pos = data.positive[idx];
                  const neu = data.neutral[idx];
                  const neg = data.negative[idx];
                  if (context.datasetIndex === 0) {
                    return this.$t('insights.Positive') + ': ' + pos + '  \u00b7  ' + this.$t('insights.Neutral') + ': ' + neu + '  \u00b7  ' + this.$t('insights.Critical') + ': ' + neg;
                  }
                  return null;
                },
                title: (items) => {
                  return items[0].label;
                },
              },
              filter: (item) => item.datasetIndex === 0,
            },
            datalabels: { display: false },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                color: '#999',
                font: { size: 10, family: 'Lato', weight: '600' },
                maxRotation: 0,
                minRotation: 0,
                autoSkip: true,
                maxTicksLimit: totalMonths > 24 ? 12 : (totalMonths > 12 ? 8 : totalMonths),
              },
              border: { display: false },
            },
            y: {
              display: false,
              stacked: false,
            },
          },
          interaction: {
            mode: 'index',
            intersect: false,
          },
          layout: {
            padding: { left: 0, right: 0, top: 4, bottom: 0 },
          },
        },
      });
      this.$nextTick(() => this.updateViewportBrush());
    },
    setupMonthObserver() {
      if (this.monthObserver) this.monthObserver.disconnect();
      const scrollEl = this.$refs.scrollContainer;
      if (!scrollEl) return;
      this.monthObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const key = entry.target.getAttribute('data-month-key');
          if (!key) return;
          if (entry.isIntersecting) {
            this.$set(this.visibleMonthKeys, key, true);
            // Mark as animated after first render to prevent re-animation on re-scroll
            this.$nextTick(() => { this.$set(this.animatedMonthKeys, key, true); });
          } else {
            const contentEl = entry.target.querySelector('.month-tags-content');
            if (contentEl) {
              this.$set(this.monthContentHeights, key, contentEl.offsetHeight);
            }
            this.$set(this.visibleMonthKeys, key, false);
          }
        });
      }, {
        root: scrollEl,
        rootMargin: '400px 0px',
      });
      this.$nextTick(() => {
        const allKeys = Object.keys(this.talking_points_monthly);
        allKeys.forEach(key => {
          const ref = this.$refs['month-' + key];
          if (!ref) return;
          const el = Array.isArray(ref) ? ref[0] : ref;
          if (el) {
            el.setAttribute('data-month-key', key);
            this.monthObserver.observe(el);
          }
        });
        // Mark initially visible months as animated after first render
        this.$nextTick(() => {
          allKeys.forEach(key => {
            if (this.visibleMonthKeys[key]) {
              this.$set(this.animatedMonthKeys, key, true);
            }
          });
        });
      });
    },
    destroyMonthObserver() {
      if (this.monthObserver) {
        this.monthObserver.disconnect();
        this.monthObserver = null;
      }
    },
    initViewportSync() {
      const scrollEl = this.$refs.scrollContainer;
      if (!scrollEl) return;
      this.teardownViewportSync();
      this.boundOnContentScroll = () => this.onContentScroll();
      scrollEl.addEventListener('scroll', this.boundOnContentScroll, { passive: true });
      this.onContentScroll();
    },
    teardownViewportSync() {
      if (this._scrollRafId) {
        cancelAnimationFrame(this._scrollRafId);
        this._scrollRafId = null;
      }
      const scrollEl = this.$refs.scrollContainer;
      if (scrollEl && this.boundOnContentScroll) {
        scrollEl.removeEventListener('scroll', this.boundOnContentScroll);
      }
      this.removeGlobalDragListeners();
    },
    onContentScroll() {
      if (this._scrollRafId) return;
      this._scrollRafId = requestAnimationFrame(() => {
        this._scrollRafId = null;
        this._doContentScroll();
      });
    },
    _doContentScroll() {
      if (this.viewportDragging) return;
      const data = this.sentimentChartData;
      if (!data) return;
      const scrollEl = this.$refs.scrollContainer;
      if (!scrollEl) return;
      const keys = data.keys;
      const containerTop = scrollEl.getBoundingClientRect().top;
      let closestIdx = 0;
      let closestDist = Infinity;
      for (let i = 0; i < keys.length; i++) {
        const ref = this.$refs['month-' + keys[i]];
        if (!ref) continue;
        const el = Array.isArray(ref) ? ref[0] : ref;
        const domEl = el && el.$el ? el.$el : el;
        if (!domEl) continue;
        const rect = domEl.getBoundingClientRect();
        const dist = Math.abs(rect.top - containerTop);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      }
      this.currentViewportIndex = closestIdx;
      this.updateViewportBrush();
    },
    updateViewportBrush() {
      const brush = this.$refs.viewportBrush;
      const chart = this.sentimentChartInstance;
      const data = this.sentimentChartData;
      if (!brush || !chart || !data) return;
      const totalMonths = data.keys.length;
      if (totalMonths === 0) return;
      const chartArea = chart.chartArea;
      if (!chartArea) return;
      const chartLeft = chartArea.left;
      const chartWidth = chartArea.right - chartArea.left;
      const segmentWidth = chartWidth / totalMonths;
      const visibleMonths = Math.max(1, Math.min(3, Math.ceil(totalMonths * 0.08)));
      const idx = Math.max(0, this.currentViewportIndex);
      const brushLeft = chartLeft + (idx * segmentWidth);
      const maxAvailableWidth = chartWidth - (idx * segmentWidth);
      const brushWidth = Math.min(Math.max(segmentWidth * visibleMonths, 24), maxAvailableWidth);
      brush.style.left = brushLeft + 'px';
      brush.style.width = brushWidth + 'px';
      brush.style.top = chartArea.top + 'px';
      brush.style.height = (chartArea.bottom - chartArea.top) + 'px';
      brush.style.display = 'block';
    },
    startViewportDrag(event) {
      this.viewportDragging = true;
      this.viewportDragStartX = event.clientX;
      this.viewportScrollStartLeft = this.currentViewportIndex;
      const brush = this.$refs.viewportBrush;
      if (brush) brush.classList.add('dragging');
      this.boundHandleViewportDrag = (e) => this.handleViewportDrag(e);
      this.boundStopViewportDrag = () => this.stopViewportDrag();
      document.addEventListener('mousemove', this.boundHandleViewportDrag);
      document.addEventListener('mouseup', this.boundStopViewportDrag);
    },
    handleViewportDrag(event) {
      if (!this.viewportDragging) return;
      const data = this.sentimentChartData;
      const chart = this.sentimentChartInstance;
      if (!data || !chart) return;
      const chartArea = chart.chartArea;
      if (!chartArea) return;
      const chartWidth = chartArea.right - chartArea.left;
      const totalMonths = data.keys.length;
      const dx = event.clientX - this.viewportDragStartX;
      const segmentWidth = chartWidth / totalMonths;
      const indexDelta = Math.round(dx / segmentWidth);
      const newIndex = Math.max(0, Math.min(totalMonths - 1, this.viewportScrollStartLeft + indexDelta));
      if (newIndex !== this.currentViewportIndex) {
        this.currentViewportIndex = newIndex;
        this.updateViewportBrush();
        const monthKey = data.keys[newIndex];
        this.scrollToMonth(monthKey, 'auto');
      }
    },
    stopViewportDrag() {
      this.viewportDragging = false;
      const brush = this.$refs.viewportBrush;
      if (brush) brush.classList.remove('dragging');
      this.removeGlobalDragListeners();
    },
    removeGlobalDragListeners() {
      if (this.boundHandleViewportDrag) {
        document.removeEventListener('mousemove', this.boundHandleViewportDrag);
        this.boundHandleViewportDrag = null;
      }
      if (this.boundStopViewportDrag) {
        document.removeEventListener('mouseup', this.boundStopViewportDrag);
        this.boundStopViewportDrag = null;
      }
    },
    scrollToMonth(monthKey, behavior) {
      const ref = this.$refs['month-' + monthKey];
      if (!ref) return;
      const el = Array.isArray(ref) ? ref[0] : ref;
      const domEl = el && el.$el ? el.$el : el;
      if (!domEl) return;
      const scrollEl = this.$refs.scrollContainer;
      if (scrollEl) {
        const containerRect = scrollEl.getBoundingClientRect();
        const elRect = domEl.getBoundingClientRect();
        const offset = elRect.top - containerRect.top + scrollEl.scrollTop;
        scrollEl.scrollTo({ top: offset, behavior: behavior || 'smooth' });
      }
    },
    toggleSentiment(type) {
      const active = this.activeSentiments;
      const activeCount = Object.values(active).filter(v => v).length;
      if (activeCount === 1 && active[type]) return;
      this.$set(this.activeSentiments, type, !active[type]);
      this.renderSentimentChart();
    },
    getSentimentType(item, monthKey) {
      if (monthKey < '2024-12') {
        if (item.sentiment > 0) return 'positive';
        if (item.sentiment < 0) return 'negative';
        return 'neutral';
      }
      if (item.sentiment === 'positive') return 'positive';
      if (item.sentiment === 'negative') return 'negative';
      return 'neutral';
    },
    destroySentimentChart() {
      this.teardownViewportSync();
      this.destroyMonthObserver();
      if (this.sentimentChartUnwatch) {
        this.sentimentChartUnwatch();
        this.sentimentChartUnwatch = null;
      }
      if (this.sentimentChartInstance) {
        this.sentimentChartInstance.destroy();
        this.sentimentChartInstance = null;
      }
    },
    setMonthly(MonthYear) {
      let split = MonthYear.split("-");
      let labelOpt = format(new Date(split[0], split[1] - 1, 1), "MMMM YYYY");
      return labelOpt;
    },
    getFontStyle(text) {
      // Thai characters
      if (text.match(/[ก-๙]/)) {
        return "Kanit";
      }
      // Chinese characters (Traditional Chinese)
      else if (text.match(/[\u4e00-\u9fff\u3400-\u4dbf]/)) {
        return "Noto Sans TC";
      }
      // Malay uses Latin script, so Lato is fine
      else {
        return "Lato";
      }
    },
    DimensionClicked(row) {
      this.$store.dispatch("dashboard/resetState");
      let data = {
        dimpic: row.dimpic,
        name: row.name,
        quest_id: row.quest_id,
      };
      this.dimensionData = data;
      this.$store.dispatch("dashboard/getDataChart", {
        user: this.user,
        quest_id: row.quest_id,
      });
      this.$store.dispatch("dashboard/getDataTalkingPoints30D", {
        user: this.user,
        category_id: row.category_id,
      });
      this.dialogDimensionVisible = true;
    },
    truncateText(text) {
      let length = this.maxLength;
      return text.length > length ? text.slice(0, length) + "..." : text;
    },
    truncateTextKeyGroup(text) {
      let length = 18;
      return text.length > length ? text.slice(0, length) + "..." : text;
    },
    activeLineHeight(value) {
      let percentage = (value / this.maximumUsers) * 100;
      return (40 / 100) * percentage;
    },
    arrayToPercentage(value) {
      const arr = [];
      let color = "bar-red";
      if (value > 70) {
        color = "bar-green";
      } else if (value > 40) {
        color = "bar-yellow";
      }
      for (let i = 0; i < 10; i++) {
        let status = (value - i * 10) * 1;
        let style = "";
        if (status > 10) {
          style = "";
        } else if (value - i * 10 > 0) {
          style = `opa-0${status * 1}`;
        } else {
          color = "";
        }
        arr.push(`${style} ${color}`);
      }
      return arr;
    },

    benchmarkBar(value) {
      if (this.checkedInxed()) {
        if (value < 0) {
          return "0";
        } else if (value > 99) {
          return "120";
        }
        return (value * 1.2).toFixed();
      } else {
        if (value < -99) {
          return "0";
        } else if (value > 99) {
          return "120";
        }
        return ((value + 100) * 0.6).toFixed();
      }
    },
    routerLink(path, tab, query) {
      if (path == "insightsWithTab" && tab) {
        // if (tab == "managers") {
        //   this.$store.commit("insightsManagers/setTabManager", true);
        // }
        this.$emit("changedTab", { path, tab });
      } else if (tab) {
        if (query) {
          this.$router.push({
            name: path,
            params: { tab: tab },
            query: { questionID: query },
          });
          this.$store.commit("dashboard/setOpenModalDashboard", true);
        } else {
          this.$router.push({ name: path, params: { tab: tab } });
        }
      } else {
        this.$message.error("Oops, Something wrong with this link.");
      }
    },
    navigateToPerformance(tab) {
      this.$router.push({ name: 'performance', params: { tab } });
    },
    navigateToSkills(tab) {
      this.$router.push({ name: 'skills', params: { tab } });
    },
    setEngagementImpactLoading() {
      if (
        this.impactRatingLoading == false &&
        this.enpsLoading == false
      ) {
        this.engagementImpactLoadingStatus = false;
      } else {
        this.engagementImpactLoadingStatus = true;
      }
    },
    setWeeklyLoadingStatus() {
      if (this.feedbackLoading == false && this.happinessLoading == false) {
        this.weeklyLoadingStatus = false;
      } else {
        this.weeklyLoadingStatus = true;
      }
    },
    benchmarkScore() {
      if (!this.checkedInxed()) {
        return 30;
      }
      return 80;
    },
    benchmarkScoreRed() {
      if (!this.checkedInxed()) {
        return 0;
      }
      return 50;
    },
    checkedInxed() {
      let index = this.benchmarkBreak != "eNPS" && this.benchmarkBreak != "NPS";

      if (index) {
        return true;
      }
      return false;
    },
    imagesBenchmark(value) {
      if (this.checkedInxed) {
        let benchmark = "Resilience";
        let type = "Critical";
        switch (this.benchmarkBreak) {
          case "WHO-5 Wellbeing Index":
            benchmark = "Wellness";
            break;
          case "Culture Index":
            benchmark = "Culture";
            break;
          case "Employee Resilience Index":
            benchmark = "Resilience";
        }
        if (value == "Excellent") {
          type = "Excellence";
        } else if (value == "Fair") {
          type = "Fair";
        }
        return `benchmark_${benchmark} - ${type}_16x16`;
      } else {
        if (value == "Excellent") {
          type = "Excellent";
          return "Promoters_16";
        } else if (value == "Fair") {
          return "Passives_16";
        }
        return "Distractors_16";
      }
    },
    detectColorTotal(type) {
      let value = 0;
      if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
        value = this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1];
      }
      if(value >= 70){
        return "green";
      }else if(value < 70 && value >= 40){
        return "yellow";
      }else{
        if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
          return "red";
        }else{
          return "";
        }
      }
    },
    detectTextTotal(type) {
      let value = 0;
      if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
        value = this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1];
      }
      if(value >= 70){
        return "GOOD";
      }else if(value < 70 && value >= 40){
        return "OKAY";
      }else{
        if(this.databarchartEngagementScore[type] && this.databarchartEngagementScore[type][this.databarchartEngagementScore[type].length - 1]){
          return "POOR";
        }else{
          return "-";
        }
      }
    },
    getScoreCount(score) {
      if (!this.enps || !this.enps.allScores) return 0;
      
      const scoreItem = this.enps.allScores.find(item => {
        return Number(item.score) === Number(score);
      });
      
      return scoreItem ? scoreItem.count : 0;
    },
    
    getScoreWidth(score) {
      if (!this.enps || !this.enps.allScores) return '0%';
      
      const count = this.getScoreCount(score);
      if (count === 0) return '0%';
      
      const counts = this.enps.allScores.map(item => item.count);
      const maxCount = counts.length > 0 ? Math.max(...counts) : 1;
      
      return `${(count * 100 / maxCount)}%`;
    },
    getScoreRange() {
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        // Return array [0, 10, 20, ..., 100] for WHO-5
        return Array.from({ length: 11 }, (_, i) => i * 10);
      }
      // Return array [0,1,2,...,10] for NPS/eNPS
      return Array.from({ length: 11 }, (_, i) => i);
    },

    formatScoreLabel(score) {
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        return score === 100 ? '100' : `${score}`;
      }
      return score.toString();
    },

    getScoreColor(score) {
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        // WHO-5 color scheme: red < 50, yellow 50-69, green >= 70
        return score >= 70 ? '#35d077' : score >= 50 ? '#fbc02d' : '#f44336';
      }
      // NPS/eNPS color scheme
      return score >= 9 ? '#35d077' : score <= 6 ? '#f44336' : '#fbc02d';
    },

    getTotalResponses() {
      if (!this.enps || !this.enps.allScores) return 0;
      
      // Use allScores array to calculate total responses
      if (this.enps.allScores && this.enps.allScores.length > 0) {
        return this.enps.allScores.reduce((sum, item) => sum + (item.count || 0), 0);
      }
      
      // Fallback to the old method if allScores is not available
      if (this.enps.type === 'WHO-5 Wellbeing Index') {
        return this.enps.excellent + this.enps.fair + this.enps.critical || 0;
      }
      return this.enps.promotors + this.enps.passives + this.enps.detractors || 0;
    },
    getPeopleImpactClass() {
      // Check if data is still loading or invalid
      if (!this.engagementpeople || !this.engagementpeople.total || this.engagementpeople.total === 0) {
        return 'highlight-text'; // No color class when loading
      }
      
      const percentage = Math.round((this.engagementpeople.counts.green + this.engagementpeople.counts.yellow) / this.engagementpeople.total * 100);
      if (isNaN(percentage)) {
        return 'highlight-text'; // No color class for invalid data
      }
      
      if (percentage >= 70) {
        return 'highlight-text highlight-green';
      } else if (percentage >= 40) {
        return 'highlight-text highlight-yellow';
      } else {
        return 'highlight-text highlight-red';
      }
    },
    getPeopleImpactText() {
      // Check if data is still loading or invalid
      if (!this.engagementpeople || !this.engagementpeople.total || this.engagementpeople.total === 0) {
        return 'Loading...';
      }
      
      const percentage = Math.round((this.engagementpeople.counts.green + this.engagementpeople.counts.yellow) / this.engagementpeople.total * 100);
      if (isNaN(percentage)) {
        return 'Loading...';
      }
      
      return `${percentage}% impactful`;
    },
    getManagerActivityClass() {
      // Check if data is still loading or invalid
      if (!this.activeOverview || !this.activeOverview.all || this.activeOverview.all === 0) {
        return 'highlight-text'; // No color class when loading
      }
      
      const percentage = Math.round((this.activeOverview.active + this.activeOverview.passive) / this.activeOverview.all * 100);
      if (isNaN(percentage)) {
        return 'highlight-text'; // No color class for invalid data
      }
      
      if (percentage >= 80) {
        return 'highlight-text highlight-green';
      } else if (percentage >= 50) {
        return 'highlight-text highlight-yellow';
      } else {
        return 'highlight-text highlight-red';
      }
    },
    getManagerActivityText() {
      // Check if data is still loading or invalid
      if (!this.activeOverview || !this.activeOverview.all || this.activeOverview.all === 0) {
        return 'Loading...';
      }
      
      const percentage = Math.round((this.activeOverview.active + this.activeOverview.passive) / this.activeOverview.all * 100);
      if (isNaN(percentage)) {
        return 'Loading...';
      }
      
      return `${percentage}% replying`;
    },
    getMiniTrend(questId) {
      const points = (this.miniTrends && this.miniTrends[questId]) ? this.miniTrends[questId] : [];
      // Only keep last 3-4
      if (points.length > 4) return points.slice(points.length - 4);
      return points;
    },
    computeSparkline(points) {
      if (!points || points.length === 0) return '';
      const w = 40, h = 16, padX = 1, padY = 1;
      const n = points.length;
      const min = Math.min(...points);
      const max = Math.max(...points);
      const range = max - min || 1;
      const stepX = (w - padX * 2) / (n - 1);
      const scaled = points.map((v, i) => {
        const x = padX + i * stepX;
        const y = padY + (h - padY * 2) * (1 - (v - min) / range);
        return `${x},${y}`;
      });
      return scaled.join(' ');
    },
    sparkColor(points) {
      if (!points || points.length < 2) return '#6c30cc';
      const diff = points[points.length - 1] - points[points.length - 2];
      return diff >= 0 ? '#27bc6c' : '#f44336';
    },
  },
};
</script>
<style scoped lang="scss">
#insightsDashboard>>>img {
  border-radius: 50%;
}

.flex.card {
  justify-content: center;
}

// Secondary-tier dropdown trigger: compact inline filter pill.
.menuOptionStyle {
  border-bottom: transparent;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  transition: background-color .15s ease, border-color .15s ease;

  &:hover {
    background-color: #f6f7f9;
  }
}

.divTable {
  flex: 1;
  margin-left: -9px;
}

.txtDetailTable {
  color: #9d9d9d;
  font-size: 14px;
  word-break: normal;
}

// .card-box {
//   margin-top: 20px;
//   margin-right: 20px;
//   width: 180px;
//   height: 100px;
//   border-radius: 5px;
//   background-color: #f4f4f4;
//   text-align: center;
//   box-sizing: border-box;
// }
.KeyGroupBox {
  margin-top: 6px;
  margin-right: 6px;
  width: 168px;
  height: 48px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  // box-shadow: 0px 2px 4px #00000029;
  box-sizing: border-box;
  overflow: hidden;
  cursor: default;
}

#insightsDashboard .KeyGroupBox img {
  border-radius: 0;
}

// .txtHeadBox {
//   padding: 21px 5px 2px 5px;
//   font-size: 36px;
//   color: #363636;
//   display: inline-block;
// }
.txtHeaderKey {
  font-size: 12px;
  color: #535353;
  font-weight: 700;
  white-space: nowrap;
}

.txtDetailKey {
  font-size: 10px;
  color: #000000;
  opacity: 0.5;
}

.txtPercent {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #999999;
}

.txtPercent.green {
  color: #27bc6c;
}

.txtPercent.red {
  color: #f44336;
}
// Fixed-width delta + trend slots so the score chips form a clean vertical
// column whether or not a row has a sparkline.
.success-score-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  .qd-score-chip { flex: none; }

  .ssc-delta {
    width: 42px;
    flex: none;
    text-align: right;
    font-size: 12px;
    font-weight: 600;

    &.green { color: #27bc6c; }
    &.red { color: #f44336; }
    &.grey { color: #9a9a9a; }
  }

  .ssc-trend {
    width: 40px;
    flex: none;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    svg { display: block; }
  }

  // "No trend yet" placeholder — a faint dotted rule that holds the slot so
  // rows without a sparkline read as intentional rather than broken.
  .ssc-trend-empty {
    display: inline-block;
    width: 18px;
    height: 0;
    border-top: 2px dotted #ccced6;
  }
}

// el-tooltip makes its trigger focusable, which paints a default blue focus
// ring around these info hints. They are non-interactive, so suppress it.
.rag-bar,
.tone-valence-bar,
.percentage-change-number,
.ssc-trend-empty,
.tp-item,
.ch-row {
  &:focus,
  &:focus-visible {
    outline: none;
  }
}


.txtPercent.grey {
  color: #747474;
}

.divCurcleStatus {
  margin-left: 5px;
  margin-right: 5px;
  width: 50px;
  height: 48px;
  text-align: center;
  border-radius: 50%;
}

.divCurcleStatus.green {
  background-color: #66bb6a;
}

.divCurcleStatus.yellow {
  background-color: #fbc02d;
}

.divCurcleStatus.red {
  background-color: #f44336;
}

.divCurclePic {
  margin-left: 5px;
  margin-right: 5px;
  width: 60px;
  height: 60px;
  text-align: center;
  border-radius: 50%;
  background-color: #f0f0f0;
}

.txtStatus {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  padding: 0px;
  line-height: 50px;
}

.txtButton {
  padding: 0;
  margin-left: 16px;
  font-size: 14px;
}

.el-button--text {
  color: #6c30cc;
}

.el-button--text:hover {
  color: #5423a2;
}

.el-button--text:active {
  color: #6c30cc;
}

.talkingPointsBox {
  height: 40px;
}

.talking-points {
  border-width: 0;
  border-radius: 16px;
  font-weight: 700;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  margin: 8px 8px 0 0;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

.GreenBoxStyle {
  color: #249d2a;
  background-color: #edffee;
}

.RedBoxStyle {
  color: #f44336;
  background-color: #ffedeb;
}

.GreyBoxStyle {
  color: #484848;
  // border-color: #b2b2b2;
  background-color: #f0f0f0;
}

// --- Talking points: sentiment-led list with a scannable colour rail ---
.tp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.tp-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 11px;
  color: #8a8a8a;

  > span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .tp-leg-mark {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    display: inline-block;
  }
  .tp-leg-mark.s-pos { background-color: #27bc6c; }
  .tp-leg-mark.s-neu { background-color: #b9bdc9; }
  .tp-leg-mark.s-neg { background-color: #f44336; }
}

.tp-list {
  margin-top: 8px;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 8px;
}

.tp-item {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #f0f1f4;
  cursor: default;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #fafbfd;
  }

  // The rail is the primary sentiment cue — a continuous colour column you
  // can scan down the left edge, far clearer than a small dot.
  .tp-rail {
    width: 4px;
    flex: none;
    border-radius: 999px;
    margin: 10px 13px 10px 2px;
  }

  &.tp-pos .tp-rail { background-color: #27bc6c; }
  &.tp-neu .tp-rail { background-color: #b9bdc9; }
  &.tp-neg .tp-rail { background-color: #f44336; }
}

.tp-text {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #3a3a3a;
  padding: 10px 6px 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.15s ease;
}

// Drillable themes: scannable at rest, with an evidence count + a chevron
// that resolves on hover so the row reads as "click for the examples".
.tp-item.tp-clickable { cursor: pointer; }
.tp-item.tp-clickable:hover .tp-text { color: #1a1a1a; }
.tp-item.tp-clickable:hover .tp-ev-go { opacity: 1; transform: translateX(0); color: #8a8a8a; }
.tp-item.tp-clickable:hover .tp-ev-count { color: #6b7280; background: #eef0f5; }

.tp-evidence {
  flex: none;
  align-self: center;
  display: flex;
  align-items: center;
  gap: 4px;
  padding-left: 10px;
}
.tp-ev-count {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #aeb2bd;
  background: #f4f5f8;
  border-radius: 999px;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.tp-ev-go {
  font-size: 13px;
  color: #c8ccd6;
  opacity: 0;
  transform: translateX(-3px);
  transition: opacity 0.15s ease, transform 0.15s ease, color 0.15s ease;
}

.buttonClose {
  padding-top: 10px;
  height: 36px;
  font-size: 16px;
  font-weight: 700;
}

.headerModalStyle {
  margin-left: 20px;
  margin-right: 52px;
  margin-top: 30px;
}

.txtMonthlyStyle {
  color: #747474;
  font-size: 16px;
  font-weight: 700;
}

.scroll-style {
  max-height: 45vh;
  padding-bottom: 1px;
  overflow-y: auto;
}

// Sentiment stream chart container
.sentiment-chart-container {
  padding: 16px 52px 12px;
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
}

.sentiment-chart-wrapper {
  position: relative;
  overflow: hidden;
}

// Viewport brush overlay
.viewport-brush {
  position: absolute;
  display: none;
  background: rgba(108, 48, 204, 0.10);
  border: 2px solid rgba(108, 48, 204, 0.35);
  border-radius: 4px;
  cursor: grab;
  z-index: 2;
  transition: left 0.15s ease, width 0.15s ease;
  user-select: none;

  &:hover {
    background: rgba(108, 48, 204, 0.16);
    border-color: rgba(108, 48, 204, 0.5);
  }

  &:active {
    cursor: grabbing;
    background: rgba(108, 48, 204, 0.20);
    border-color: rgba(108, 48, 204, 0.6);
    transition: none;
  }
}

.viewport-brush.dragging {
  transition: none !important;
  background: rgba(108, 48, 204, 0.22);
  border-color: rgba(108, 48, 204, 0.6);
  cursor: grabbing;
}

.viewport-brush-handle {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  border-radius: 2px;
  background: rgba(108, 48, 204, 0.4);

  &.left { left: 2px; }
  &.right { right: 2px; }
}

.month-tags-content {
  min-height: 40px;
}

.month-tags-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, rgba(0,0,0,0.01) 0%, rgba(0,0,0,0.02) 50%, rgba(0,0,0,0.01) 100%);
  border-radius: 8px;
  margin: 4px 0;
}

// Sentiment breakdown bar per month
.sentiment-breakdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 8px 52px 16px;
}

.sentiment-bar {
  display: flex;
  width: 100%;
  max-width: 500px;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
}

.sentiment-bar-segment {
  transition: width 0.4s ease;
  &.positive { background: #249d2a; }
  &.neutral { background: #c0c0c0; }
  &.negative { background: #f44336; }
}

.sentiment-summary {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  letter-spacing: 0.02em;
}

// Legend filter states
.legend-tag-inactive {
  opacity: 0.3;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.legend-tag-active {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

// Tag visibility based on sentiment filter
.tag-filtered-out {
  opacity: 0.12;
  transform: scale(0.95);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

// Staggered tag entrance animation
.tag-enter {
  animation: tagFadeIn 0.3s ease forwards;
  opacity: 0;
}

@keyframes tagFadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.scrollingStyle2 {
  max-height: 55vh;
}

.txtDimensionDesc {
  color: #626262;
  font-size: 14px;
}

.barchart-style {
  background-color: rgba(0, 0, 0, 0.03);
  // box-shadow: 0px 2px 4px #00000029;
  border-radius: 24px;
  margin-top: 10px;
  width: calc(100% - 42px);
  height: 186px;
  padding: 10px 24px 12px 18px;
}

.tenure-period {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: baseline;
  margin-top: 4px;
  color: #777;
  font-size: 11px;
  line-height: 1.4;
}

.tenure-data-warning {
  width: calc(100% - 42px);
  margin-top: 8px;
  color: #8a4b08;
  font-size: 11px;
  line-height: 1.4;
}

.chart-legend {
  display: flex;
  padding: 4px 12px;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  align-items: center;
  margin: 6px 0 0 12px;
}

.chart-legend .symbol {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin: 0 8px;
}

.symbol.blue {
  background-color: #00ACC1;
}

.symbol.orange {
  background-color: #E45006;
}

.symbol.pink {
  background-color: #E048DA;
}

.chart-legend .tag {
  margin-left: 8px;
  padding: 1px 8px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
}

.chart-legend .tag.yellow {
  background-color: rgba(251, 192, 45, .20);
  color: #C3931E;
}

.chart-legend .tag.red {
  background-color: rgba(244, 67, 54, .20);
  color: #C73A2F;
}

.chart-legend .tag.green {
  background-color: rgba(102, 187, 106, .20);
  color: #559B58;
}

.barchart-engagement-style {
  width: calc(100% - 12px);
  height: 120px;
}

.chartStyle {
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 24px;
}

.tooltipStyle {
  width: 375px;
  padding: 8px;
}

/* จัดการรายการภายใน Tooltip */
.tooltip-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* สไตล์ตัวเลขวงกลม (OI) */
.oi-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background-color: rgba(108, 48, 204, 0.2); /* สีพื้นหลัง */
  // opacity: 0.2;
  color: #6C30CC; /* สีตัวอักษร */
  font-weight: bold;
  margin-right: 12px;
}

/* ข้อความ */
.tooltip-item {
  display: flex;
  align-items: flex-start;
  padding-top : 11px;
}

.tooltip-item p {
  margin: 0;
  font-size: 12px;
}

.tooltipHelpStyle {
  width: 218px;
}

.tooltipKeyGroupStyle {
  max-width: 250px;
}

.grid-2-second {
  display: grid;
  grid-template-columns: 2fr 3fr;
  column-gap: 44px;
  align-items: start;
}

.icon-beta {
  padding: 5px 12px 3px;
  border-radius: 12px;
  background-color: #1ec182;
  color: #fff;
  font-size: 10px;
  font-weight: 400;
  margin-left: 12px;
}

.tone-body {
  margin-top: 14px;
  align-items: center;

  // Subtle ranked index — keeps the "top tones" reading without a second
  // movement indicator competing with the trend column on the right.
  .tone-rank {
    width: 16px;
    font-size: 12px;
    font-weight: 600;
    color: #b6b6bf;
    text-align: center;
    flex: none;
  }

  // Valence status marker (replaces the per-tone emoji). Pairs with the
  // valence-coloured bar and the mood-meter legend above.
  .tone-valence-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin: 0 11px 0 9px;
    flex: none;

    &.v-positive { background-color: #27bc6c; }
    &.v-neutral { background-color: #cbcdd6; }
    &.v-negative { background-color: #f44336; }
  }

  .tone-name {
    width: 116px;
    font-size: 14px;
    font-weight: 500;
    color: #2f2f2f;
    flex: none;
  }

  .percentage-bar {
    width: 92px;
    flex: none;
  }

  .percentage-number {
    width: 38px;
    text-align: end;
    margin-left: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #5a5a5a;
    flex: none;
  }

  .percentage-change-number {
    margin-left: 10px;
    text-align: center;
    border-radius: 4px;
    font-size: 12px;
    padding: 4px 4px 2px;
  }

  .percentage-change-number.up {
    background-color: #12c29a;
    color: #fff;
  }

  .percentage-change-number.down {
    background-color: #e2e2e2;
    color: #5c5c5c;
  }
  
  .percentage-change-number.same {
    background-color: #f5f5f5;
    color: #666;
  }

  // Honest good/bad change semantics, rendered as light coloured text (no
  // pill) so it matches the engagement table's delta treatment and keeps the
  // dense tone list clean.
  .percentage-change-number.good,
  .percentage-change-number.bad,
  .percentage-change-number.neutral {
    background-color: transparent;
    padding: 0;
    min-width: 40px;
    text-align: right;
    font-weight: 700;
  }
  .percentage-change-number.good { color: #27bc6c; }
  .percentage-change-number.bad { color: #f44336; }
  .percentage-change-number.neutral { color: #9aa0ad; }

  .tone-change-arrow {
    font-size: 11px;
    margin-right: 1px;
  }
}

// --- Tone balance "mood meter" ---
// Slim pill bar (matches the engagement team-health bar) with no inline
// numbers; the exact split is on hover. Mood label + legend share one row.
.tone-valence {
  margin-top: 16px;
  margin-bottom: 6px;

  .tone-valence-bar {
    display: flex;
    width: 100%;
    height: 10px;
    border-radius: 999px;
    overflow: hidden;
    background: #eef0f5;
    cursor: default;
  }

  .tvseg { height: 100%; }
  .tvseg.tv-pos { background-color: #27bc6c; }
  .tvseg.tv-neu { background-color: #d3d1c7; }
  .tvseg.tv-neg { background-color: #f44336; }

  .tone-valence-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px 12px;
    margin-top: 9px;
  }

  .tone-valence-mood {
    font-size: 13px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .tone-valence-mood.good { color: #0f6e56; }
  .tone-valence-mood.bad { color: #a32d2d; }

  .tone-valence-legend {
    font-size: 11px;
    color: #8a8a8a;
    display: inline-flex;
    align-items: center;
    gap: 5px;

    .tvdot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
      margin-left: 6px;
    }
    .tvdot.tv-pos { background-color: #27bc6c; }
    .tvdot.tv-neu { background-color: #d3d1c7; }
    .tvdot.tv-neg { background-color: #f44336; }
    .tvdot:first-child { margin-left: 0; }
  }
}

// --- Communication Health: two compact status rows (quality + safety) ---
.ch-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;

  .ch-head-help {
    font-size: 13px;
    color: #c2c6d0;
    cursor: help;
    transition: color 0.15s ease;
    &:hover { color: #8a8a8a; }
  }
}

.ch-strip {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .ch-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 14px;
    border-radius: 12px;
    border: 1px solid #eef0f5;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  }

  .ch-row.is-warn { background: #fffdf7; border-color: #f3e6c8; }
  .ch-row.is-danger { background: #fef6f5; border-color: #f3d4d1; }

  .ch-row.is-clickable { cursor: pointer; }
  .ch-row.is-clickable:hover {
    box-shadow: 0 6px 18px rgba(20, 23, 33, 0.07);
    transform: translateY(-1px);
  }
  .ch-row.is-warn.is-clickable:hover { border-color: #e9d29a; }
  .ch-row.is-danger.is-clickable:hover { border-color: #ecbab5; }
  .ch-row.is-clickable:hover .ch-go { transform: translateX(2px); color: #8a8a8a; }

  // status glyph in a soft tinted tile
  .ch-status {
    flex: none;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    font-size: 16px;
  }
  .ch-row.is-ok .ch-status { background: #eafaf3; color: #1aae74; }
  .ch-row.is-warn .ch-status { background: #fdf2dd; color: #c98a12; }
  .ch-row.is-danger .ch-status { background: #fde7e5; color: #cc3030; }

  .ch-label {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    line-height: 1.25;
  }
  .ch-label-title { font-size: 13px; font-weight: 700; color: #2f2f2f; }
  .ch-label-sub { font-size: 11.5px; font-weight: 500; color: #9a9a9a; margin-top: 1px; }

  .ch-meta {
    flex: none;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .ch-count { font-size: 15px; font-weight: 800; color: #2f2f2f; font-variant-numeric: tabular-nums; }
  .ch-clear { font-size: 12px; font-weight: 600; color: #1a8a63; }
  .ch-flag {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #cc3030;
    background: #fde7e5;
    padding: 3px 8px;
    border-radius: 999px;
  }
  .ch-go {
    font-size: 13px;
    color: #c8ccd6;
    transition: transform 0.15s ease, color 0.15s ease;
  }
}

// --- Engagement-by-dimension RAG proportion bar ---
.rag-cell {
  width: 100%;
  padding-right: 12px;

  .rag-bar {
    display: flex;
    width: 100%;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    background: #eef0f5;
  }

  .rag-seg { height: 100%; }

  .rag-caption {
    margin-top: 5px;
    font-size: 11px;
    color: #8a8a8a;
    line-height: 1.4;
  }

  .rag-emph { font-weight: 700; color: #5f5e5a; }
  .rag-emph-warn { color: #a32d2d; }
  .rag-legend { color: #a6a6ad; }
}
.rag-caption {
  font-size: 11px;
  color: #8a8a8a;
}

// --- Engagement-by-dimension team drill-down (expand row) ---
.dim-teams-panel {
  padding: 2px 24px 14px 24px;

  .dim-teams-head {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 10px;
  }

  .dim-teams-count {
    font-size: 12px;
    font-weight: 700;
    color: #a32d2d;
  }

  .dim-teams-empty {
    padding: 4px 0 2px;
  }

  .dim-teams-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 40px;
    align-content: start;
    max-width: 1120px;
    max-height: 300px;
    overflow-y: auto;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  .dim-team-row {
    display: flex;
    align-items: center;
    padding: 7px 10px;
    border-radius: 8px;
    transition: background-color 0.12s ease;

    &:hover { background: #fafbfc; }
  }

  .dim-team-dot {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    flex: none;
    margin-right: 10px;
  }

  .dim-team-name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    color: #4a4a4a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dim-team-total {
    flex: none;
    font-size: 11px;
    color: #a6a6ad;
    margin: 0 10px;
  }
}

// --- Tenure mid-tenure callout ---
.tenure-callout {
  align-items: flex-start;
  gap: 9px;
  margin-top: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  background: #fdeceb;

  .tenure-callout-icon {
    color: #a32d2d;
    font-size: 16px;
    margin-top: 1px;
    flex: none;
  }

  .tenure-callout-text {
    font-size: 12px;
    color: #791f1f;
    line-height: 1.45;

    .fw-700 { font-weight: 700; }
  }
}

.quality-check-container {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 8px;
  padding: 20px 24px;
  margin-top: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  .quality-counters-inline {
    display: flex;
    gap: 40px;
    align-items: center;
    justify-content: flex-start;
  }
  
  .quality-counter-inline {
    display: flex;
    align-items: center;
    
    .counter-emoji {
      width: 24px;
      margin-right: 10px;
      text-align: center;
      font-size: 16px;
    }
    
    .counter-label {
      width: 90px;
      margin-right: 16px;
      font-weight: 500;
    }
    
    .counter-value {
      padding: 4px 12px;
      border-radius: 6px;
      margin-right: 12px;
      min-width: 40px;
      text-align: center;
      font-weight: 700;
      font-size: 15px;
      
      &.success {
        background-color: #ecfdf5;
        color: #059669;
        border: 1px solid #bbf7d0;
      }
      
      &.warning {
        background-color: #fefbeb;
        color: #d97706;
        border: 1px solid #fde68a;
      }
      
      &.danger {
        background-color: #fef2f2;
        color: #dc2626;
        border: 1px solid #fecaca;
      }
    }
    
    .counter-trend {
      opacity: 0.9;
      font-weight: 700;
      font-size: 13px;
      padding: 2px 6px;
      border-radius: 4px;
      
      .trend-down {
        color: #059669;
        background: rgba(5, 150, 105, 0.1);
      }
      
      .trend-up {
        color: #dc2626;
        background: rgba(220, 38, 38, 0.1);
      }
      
      .trend-same {
        color: #6b7280;
        background: rgba(107, 114, 128, 0.1);
      }
    }
  }
}

.grid-2-tenure {
  display: grid;
  grid-template-columns: 652px auto;
}

.grid-3-engagement {
  display: grid;
  grid-template-columns: 33% 33% 33%;
}

.past-12-hours {
  .card {
    padding: 16px 18px 18px;
    margin-top: 8px;
    min-height: 188px;
    color: #fff;
    border-radius: 24px;
    background-color: #6c30cc;
  }

  .rt-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .rt-eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.72);
  }

  .rt-live {
    display: inline-flex;
    align-items: center;
  }

  .rt-live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4fe39b;
    animation: rtPulse 2s ease-out infinite;
  }

  .rt-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }

  .rt-metric-num {
    font-size: 30px;
    font-weight: 700;
    line-height: 1;
  }

  .rt-metric-cap {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.72);
    margin-top: 4px;
  }

  .rt-spark {
    position: relative;
    flex: 1;
    max-width: 150px;
    height: 46px;
  }

  .rt-list-head {
    display: flex;
    justify-content: space-between;
  }

  .rt-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.16);
    margin: 6px 0 8px;
  }

  .rt-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    padding: 5px 8px;
    margin: 0 -8px;
    border-radius: 7px;
    transition: background 0.15s ease;
  }

  .rt-row:hover {
    background: rgba(255, 255, 255, 0.09);
  }

  .rt-row-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4fe39b;
    flex: none;
  }

  .rt-row-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rt-row-val {
    margin-left: auto;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .rt-empty {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    padding: 12px 0 4px;
  }
}

@keyframes rtPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(79, 227, 155, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(79, 227, 155, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(79, 227, 155, 0);
  }
}

.top-contariner {

  // display: grid;
  // grid-template-columns: auto auto auto;
  .x-line {
    width: 100%;
    height: 1px;
    background-color: rgba($color: #000000, $alpha: 0.1);
  }

  .card-contariner {
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow:
      0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
      0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
      0px 1.5px 4px rgba(0, 0, 0, 0.015),
      0px 2.7px 7.1px rgba(0, 0, 0, 0.018),
      0px 5px 13.4px rgba(0, 0, 0, 0.022),
      0px 12px 32px rgba(0, 0, 0, 0.03);
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    padding: 24px 20px 16px;
    margin-top: 8px;
    height: 234px;
    transition: box-shadow 0.3s ease;

    &:hover {
      box-shadow:
        0px 0.4px 1.1px rgba(0, 0, 0, 0.01),
        0px 1px 2.6px rgba(0, 0, 0, 0.015),
        0px 1.9px 5px rgba(0, 0, 0, 0.018),
        0px 3.4px 8.9px rgba(0, 0, 0, 0.022),
        0px 6.3px 16.8px rgba(0, 0, 0, 0.027),
        0px 15px 40px rgba(0, 0, 0, 0.035);
    }
  }

  .label-change {
    font-size: 11px;
    font-weight: 700;
    margin-top: 4px;
    margin-left: 3px;
    white-space: nowrap;
  }

  // .engagement,
  // .weekly-status,
  // .activity {
  // }
  .engagement {
    width: calc(100% - (460px + 32px));



    .engagement-left {
      width: calc(50% - 8px);
      padding-right: 12px;
      border-right: 1px solid rgba($color: #000000, $alpha: 0.08);
      position: relative;
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        right: -1px;
        top: 20%;
        bottom: 20%;
        width: 1px;
        background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.1), transparent);
      }
    }

    .engagement-right {
      width: calc(50% - 8px);
      padding-left: 16px;
      position: relative;
      overflow: hidden;


    }

    .benchmark {
      width: 46px;
      text-align: center;
      padding: 10px 0 8px;
      font-size: 16px;
      font-weight: 700;
      color: #fff;
      border-radius: 4px;
      margin-right: 16px;
      background-color: #e0e0e0;
      cursor: help;
    }

    .benchmark.green {
      background-color: #35d077;
    }

    .benchmark.red {
      background-color: #f44336;
    }

    .benchmark.yellow {
      background-color: #fbc02d;
    }

          .tooltip-legend-item {
        display: flex;
        align-items: flex-start;
        margin: 8px 0;
        
        .tooltip-color {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          margin-right: 10px;
          margin-top: 2px;
          flex-shrink: 0;
          
          &.bg-green {
            background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
          }
          
          &.bg-yellow {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          }
          
          &.bg-red {
            background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
          }
        }
      
      > div {
        flex: 1;
        line-height: 1.3;
        
        strong {
          display: block;
          margin-bottom: 2px;
        }
      }
    }

    .section-header {
      margin-bottom: 8px;
      
      .section-title {
        font-size: 13px;
        font-weight: 600;
        gap: 4px;
        transition: color 0.2s ease;
        
        &:hover {
          color: #6c30cc;
        }
        
        i {
          font-size: 9px;
          opacity: 0.7;
        }
      }
    }

    .metric-summary {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-top: 6px;
      
      .highlight-text {
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 12px;
        white-space: nowrap;
        
        &.highlight-green {
          color: #059669 !important;
          background: rgba(5, 150, 105, 0.1) !important;
        }
        
        &.highlight-yellow {
          color: #d97706 !important;
          background: rgba(217, 119, 6, 0.1) !important;
        }
        
        &.highlight-red {
          color: #dc2626 !important;
          background: rgba(220, 38, 38, 0.1) !important;
        }
      }
    }

    .stacked-bar-container {
      margin: 0 0 8px 0;

      .stacked-bar {
        display: flex;
        width: 100%;
        height: 20px;
        border-radius: 4px;
        overflow: hidden;
        background-color: #f8f9fa;
        margin-bottom: 6px;
        box-shadow: 
          0 2px 4px rgba(0, 0, 0, 0.06),
          inset 0 1px 2px rgba(0, 0, 0, 0.05);
        border: 1px solid rgba(0, 0, 0, 0.04);

        .bar-segment {
          height: 100%;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: help;
          position: relative;

          &:hover {
            filter: brightness(1.1);
            transform: scaleY(1.08);
            z-index: 1;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          }

          &:first-child {
            border-radius: 4px 0 0 4px;
          }

          &:last-child {
            border-radius: 0 4px 4px 0;
          }

          &:only-child {
            border-radius: 4px;
          }

          // Add subtle borders between segments
          &:not(:last-child)::after {
            content: '';
            position: absolute;
            right: 0;
            top: 4px;
            bottom: 4px;
            width: 1px;
            background: rgba(255, 255, 255, 0.3);
          }
        }

        .bar-green {
          background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
        }

        .bar-yellow {
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
        }

        .bar-red {
          background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
        }
      }

      .bar-legend {
        display: flex;
        justify-content: space-around;
        align-items: center;
        padding: 2px 4px;
        background: rgba(0, 0, 0, 0.02);
        border-radius: 8px;
        overflow: hidden;
        flex-wrap: nowrap;

        .legend-item {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 2px 4px;
          border-radius: 4px;
          transition: background-color 0.2s ease;
          flex: 1;
          justify-content: center;
          min-width: 0;

          &:hover {
            background: rgba(0, 0, 0, 0.04);
          }

          .legend-color {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            border: 1px solid rgba(255, 255, 255, 0.8);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            flex-shrink: 0;

            &.bg-green {
              background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
            }

            &.bg-yellow {
              background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            }

            &.bg-red {
              background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
            }
          }

          .legend-text {
            font-size: 10px;
            font-weight: 500;
            color: #6b7280;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    .point-bar {
      width: 120px;
      border-radius: 6px;
      height: 6px;
      background-color: rgba($color: #000000, $alpha: 0.2);

      .value {
        position: relative;
        border-radius: 6px;
        height: 6px;
        background: #35d077;
      }

      .value.benchmark {
        position: relative;
        height: 6px;
        top: -6px;
        background: transparent;

        &::before {
          content: "";
          position: absolute;
          right: -1px;
          top: -3px;
          border-right: 2px solid #6c30cc;
          height: 12px;
        }
      }
    }
  }

  .weekly-status {
    margin-left: 16px;
    width: 230px;
  }

  .activity {
    margin-left: 16px;
    width: 230px;

    .bar-contariner {
      .bar {
        border-radius: 6px;
        width: 6px;
        height: 24px;
        margin-right: 2px;
        background-color: rgba($color: #000000, $alpha: 0.1);
      }

      .bar-green {
        background-color: #35d077;
      }

      .bar-yellow {
        background-color: #fbc02d;
      }

      .bar-red {
        background-color: #f44336;
      }
    }
  }
}

.score-distribution-tooltip {
  min-width: 280px !important;
  padding: 16px !important;
  background: #2d2d2d !important;
  border-radius: 4px !important;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.2) !important;

  .tooltip-title {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #000000;
    text-align: left;
  }

  .score-bars {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .score-bar-item {
      display: grid;
      grid-template-columns: 30px 30px 1fr;
      align-items: center;
      height: 20px;

      .score-info {
        display: contents;

        .score-label {
          font-size: 12px;
          font-weight: 500;
          color: rgba(255,255,255,0.7);
          padding-right: 0px;
        }

        .score-count {
          font-size: 11px;
          color: rgba(255,255,255,0.5);
        }
      }

      .score-bar-wrapper {
        position: relative;
        background-color: rgba(255,255,255,0.1);
        border-radius: 2px;
        height: 3px;
        margin-top: 2px;

        .score-bar {
          position: absolute;
          left: 0;
          top: 0;
          height: 100%;
          transition: width 0.3s ease;
          border-radius: 2px;
          width: 4px;
        }
      }
    }
  }
}

.horizontal-histogram {
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.score-labels, .histogram-bars, .score-counts {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
}

.score-labels {
  margin-bottom: 4px;
}

.score-counts {
  margin-top: 4px;
}

.score-label-item, .score-count-item {
  flex: 1;
  text-align: center;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.7);
  font-weight: 500;
}

.score-count-item {
  color: rgba(0, 0, 0, 0.5);
  font-size: 10px;
}

.histogram-bars {
  height: 120px;
  align-items: flex-end;
  margin: 10px 0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 1px;
    background-color: rgba(0, 0, 0, 0.1);
  }
  
  /* Add horizontal grid lines for better readability */
  &::after {
    box-shadow: 0 30px 0 rgba(0, 0, 0, 0.03),
                0 60px 0 rgba(0, 0, 0, 0.03),
                0 90px 0 rgba(0, 0, 0, 0.03);
  }
}

.histogram-bar {
  flex: 1;
  margin: 0 6px;
  min-height: 1px;
  transition: height 0.3s ease, transform 0.2s ease;
  width: 8px;
  border-radius: 4px 4px 0 0;
  position: relative;
  
  &:hover {
    transform: scaleY(1.05);
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
  
  &::after {
    content: attr(data-value);
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: rgba(0, 0, 0, 0.7);
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
}

// Focus & Goal Alignment Section Styles
.focus-alignment-section {
  width: 100%;
}

.section-headers-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  padding-bottom: 8px;
}

.section-header-group {
  display: flex;
  align-items: center;
}

.section-header-focus {
  // Spans first two columns worth
}

.section-header-learning {
  // Spans last column
}

.focus-alignment-cards {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-top: 8px;
}

.focus-card {
  background: #fff;
  border-radius: 6px;
  padding: 14px 18px;
  box-shadow:
    0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
    0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
    0px 1.5px 4px rgba(0, 0, 0, 0.015),
    0px 2.7px 7.1px rgba(0, 0, 0, 0.018),
    0px 5px 13.4px rgba(0, 0, 0, 0.022),
    0px 12px 32px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow:
      0px 0.4px 1.1px rgba(0, 0, 0, 0.01),
      0px 1px 2.6px rgba(0, 0, 0, 0.015),
      0px 1.9px 5px rgba(0, 0, 0, 0.018),
      0px 3.4px 8.9px rgba(0, 0, 0, 0.022),
      0px 6.3px 16.8px rgba(0, 0, 0, 0.027),
      0px 15px 40px rgba(0, 0, 0, 0.035);
  }
}

.focus-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.focus-card-loading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.focus-card-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.focus-metric {
  display: flex;
  align-items: center;
}

.weekly-change-wrapper {
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  padding-left: 16px;
  border-left: 1px solid #E4E7ED;
  line-height: 1.1;
}

.weekly-metric-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.weekly-value {
  font-size: 14px;
  font-weight: 700;
  color: #303133;
}

.change-label {
  font-size: 10px;
  color: #909399;
  font-weight: 500;
}

.focus-metric-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

.focus-metric-value-unknown {
  color: #909399;
  font-size: 20px;
}

.focus-metric-na {
  color: #909399 !important;
}

.focus-progress-bar {
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 6px;
}

.focus-progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease, background-color 0.3s ease;
}

.focus-metric-label {
  font-size: 11px;
  color: #606266;
  margin-top: 2px;
}

.learning-stats {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px !important;
}

.learning-stat {
  display: flex;
  align-items: center;
  gap: 3px;
}

.learning-stat-value {
  font-weight: 600;
  color: #303133;
}

.learning-stat-separator {
  color: #dcdfe6;
}

.focus-na-message {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #909399;
  margin-top: 2px;
  
  i {
    font-size: 11px;
    color: #c0c4cc;
  }
}

// Week-over-week change indicator styles (inline with main score)
.label-change {
  font-size: 11px;
  font-weight: 700;
  margin-left: 0;
}

.text-green {
  color: #35D077;
}

.text-red {
  color: #F44336;
}

.fc-6A6A6A {
  color: #6A6A6A;
}

</style>

<!-- Global styles for tooltip content -->
<style>
/* Compact, refined tooltip for the insights inline hints — a single clean box
   instead of Element's default double-bordered popper. Tooltip poppers render
   at the document body, so these rules live in the global (unscoped) block. */
.el-tooltip__popper.hv-tip {
  padding: 9px 12px !important;
  background: #ffffff !important;
  border: 1px solid #e6e8ee !important;
  border-radius: 8px !important;
  box-shadow: 0 6px 22px rgba(20, 23, 33, 0.12) !important;
  color: #4a4a4a !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 1.5 !important;
  max-width: 240px !important;
}
.el-tooltip__popper.hv-tip[x-placement^="top"] .popper__arrow { border-top-color: #e6e8ee !important; }
.el-tooltip__popper.hv-tip[x-placement^="top"] .popper__arrow::after { border-top-color: #ffffff !important; }
.el-tooltip__popper.hv-tip[x-placement^="bottom"] .popper__arrow { border-bottom-color: #e6e8ee !important; }
.el-tooltip__popper.hv-tip[x-placement^="bottom"] .popper__arrow::after { border-bottom-color: #ffffff !important; }

.tooltip-emoji-detail {
  max-width: 300px;
}

/* Global tooltip color indicators for Element UI tooltips */
.tooltip-legend-item {
  display: flex;
  align-items: flex-start;
  margin: 8px 0;
}

.tooltip-color {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 10px;
  margin-top: 2px;
  flex-shrink: 0;
}

.tooltip-color.bg-green {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
}

.tooltip-color.bg-yellow {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.tooltip-color.bg-red {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
}

#insightsDashboard>div>div:nth-child(1)>div>div>input {
  height: 52px !important;
  font-size: 18px;
  font-weight: 400;
  color: #4a4a4a;
}

#insightsDashboard>div>div>div.showOption>ul>li>div {
  height: 40px !important;
  line-height: 40px !important;
}

#insightsDashboard .el-dialog {
  width: 1024px;
}

#insightsDashboard .el-dialog__body {
  padding: 10px 20px;
}

#insightsDashboard .el-dialog__footer {
  padding-top: 0px;
}

/* Communication Health detail dialog — reuses the app's qd-* eyebrow/title
   vocabulary, with per-team proportion rows and severity-tinted accents
   (amber = quality/gibberish, red = safety/toxic). Dialog chrome overrides
   are id-prefixed so they beat the page-level #insightsDashboard .el-dialog
   rules above. */
#insightsDashboard .comm-health-dialog {
  width: 460px;
  max-width: calc(100vw - 32px);
  border-radius: 16px;
}
#insightsDashboard .comm-health-dialog .el-dialog__header { padding: 0; }
#insightsDashboard .comm-health-dialog .el-dialog__body { padding: 24px 26px 6px; }
#insightsDashboard .comm-health-dialog .el-dialog__footer { padding: 6px 26px 22px; }

.comm-health-dialog .ch-dlg-head { margin-bottom: 18px; }
.comm-health-dialog .ch-dlg-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin-top: 7px;
}
.comm-health-dialog .ch-dlg-title { font-size: 18px; font-weight: 800; color: #1a1a1a; }
.comm-health-dialog .ch-dlg-count {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
}
.comm-health-dialog .ch-dlg-count.is-warn { color: #b5790f; background: #fdf2dd; }
.comm-health-dialog .ch-dlg-count.is-danger { color: #cc3030; background: #fde7e5; }
.comm-health-dialog .ch-dlg-desc { margin-top: 9px; font-size: 13px; line-height: 1.5; color: #626262; }

.comm-health-dialog .ch-dlg-block { margin-top: 20px; }
.comm-health-dialog .ch-dlg-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.comm-health-dialog .ch-dlg-teamcount { font-size: 11px; font-weight: 700; color: #9a9a9a; }

.comm-health-dialog .ch-dlg-rows {
  max-height: 236px;
  overflow-y: auto;
  border: 1px solid #eef0f5;
  border-radius: 12px;
}
.comm-health-dialog .ch-dlg-row {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) 84px 34px;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border-bottom: 1px solid #f2f3f7;
}
.comm-health-dialog .ch-dlg-row:last-child { border-bottom: none; }
.comm-health-dialog .ch-dlg-rank { font-size: 12px; font-weight: 700; color: #c2c6d0; text-align: center; }
.comm-health-dialog .ch-dlg-team {
  font-size: 13px;
  font-weight: 600;
  color: #2f2f2f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.comm-health-dialog .ch-dlg-bar {
  display: block;
  height: 6px;
  border-radius: 999px;
  background: #eef0f5;
  overflow: hidden;
}
.comm-health-dialog .ch-dlg-fill { display: block; height: 100%; border-radius: 999px; }
.comm-health-dialog .ch-dlg-fill.is-warn { background: #f0b429; }
.comm-health-dialog .ch-dlg-fill.is-danger { background: #e0584f; }
.comm-health-dialog .ch-dlg-chip {
  justify-self: end;
  min-width: 26px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
}
.comm-health-dialog .ch-dlg-chip.is-warn { color: #b5790f; background: #fdf2dd; }
.comm-health-dialog .ch-dlg-chip.is-danger { color: #cc3030; background: #fde7e5; }

.comm-health-dialog .ch-dlg-actions-head { margin-bottom: 8px; }
.comm-health-dialog .ch-dlg-actions { margin: 0; padding-left: 0; list-style: none; }
.comm-health-dialog .ch-dlg-actions li {
  position: relative;
  padding-left: 18px;
  margin-bottom: 7px;
  font-size: 13px;
  line-height: 1.5;
  color: #4a4a4a;
}
.comm-health-dialog .ch-dlg-actions li:last-child { margin-bottom: 0; }
.comm-health-dialog .ch-dlg-actions li::before {
  content: "";
  position: absolute;
  left: 3px;
  top: 8px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #c8ccd6;
}

/* shared trend chip — used by both the status rows and the dialog header */
.ch-delta {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.ch-delta i { font-size: 11px; }
.ch-delta.is-good { color: #1aae74; }
.ch-delta.is-bad { color: #f44336; }

/* Talking point detail dialog — eyebrow + sentiment pill + the supporting
   examples as a readable evidence list. Sentiment accents match the list
   rails (green = positive, red = critical, grey = neutral). Chrome overrides
   are id-prefixed to beat the page-level #insightsDashboard .el-dialog rules. */
#insightsDashboard .tp-dialog {
  width: 480px;
  max-width: calc(100vw - 32px);
  border-radius: 16px;
}
#insightsDashboard .tp-dialog .el-dialog__header { padding: 0; }
#insightsDashboard .tp-dialog .el-dialog__body { padding: 24px 26px 6px; }
#insightsDashboard .tp-dialog .el-dialog__footer { padding: 6px 26px 22px; }

.tp-dialog .tpd-head { margin-bottom: 18px; }
.tp-dialog .tpd-eyebrow-row { display: flex; align-items: center; gap: 10px; }
.tp-dialog .tpd-sentiment {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
}
.tp-dialog .tpd-sent-dot { width: 7px; height: 7px; border-radius: 50%; }
.tp-dialog .tpd-sentiment.s-pos { color: #1f9d5c; background: #e7f7ee; }
.tp-dialog .tpd-sentiment.s-pos .tpd-sent-dot { background: #27bc6c; }
.tp-dialog .tpd-sentiment.s-neg { color: #cc3030; background: #fdeceb; }
.tp-dialog .tpd-sentiment.s-neg .tpd-sent-dot { background: #f44336; }
.tp-dialog .tpd-sentiment.s-neu { color: #6b7280; background: #eef0f3; }
.tp-dialog .tpd-sentiment.s-neu .tpd-sent-dot { background: #b9bdc9; }

.tp-dialog .tpd-title { margin-top: 12px; font-size: 18px; font-weight: 800; line-height: 1.4; color: #1a1a1a; }

.tp-dialog .tpd-block-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.tp-dialog .tpd-count {
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #8a8a8a;
  background: #f1f2f6;
  border-radius: 999px;
}
.tp-dialog .tpd-examples {
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tp-dialog .tpd-ex {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  padding: 12px 14px;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  background: #fcfcfd;
}
.tp-dialog .tpd-ex-num {
  flex: none;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  border-radius: 50%;
}
.tp-dialog .tpd-ex-num.s-pos { color: #1f9d5c; background: #e7f7ee; }
.tp-dialog .tpd-ex-num.s-neg { color: #cc3030; background: #fdeceb; }
.tp-dialog .tpd-ex-num.s-neu { color: #6b7280; background: #eef0f3; }
.tp-dialog .tpd-ex-text { margin: 0; font-size: 13px; line-height: 1.6; color: #4a4a4a; }
</style>
